import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, Outlet, useParams, useNavigate, NavLink } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Crown, BookOpen, GraduationCap, Wallet, FileText, Loader2,
  Plus, Trash2, Eye, EyeOff, RefreshCw, Save, Wand2,
  ChevronRight, ArrowLeft, Music, Globe, AlertCircle, CheckCircle2, Search,
  AlertTriangle, Download, Upload, TrendingUp, BarChart3, Image as ImageIcon, Gift,
  Video as VideoIcon, Link as LinkIcon,
  Film, CreditCard, Copy, ExternalLink, ShieldAlert,
  Mail, Send, Users, Filter, FileDown, Sparkles,
  Smartphone, Unlock,
} from "lucide-react";
import { apiGet, apiPost, apiPatch, apiDelete, API, api } from "../lib/api";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

// Light-purple admin chrome — separated from the user-facing lavender VIP theme
// so admin actions feel "operational" not "celebratory".
const ADMIN = {
  bg: "#F7F5FB",
  card: "#FFFFFF",
  primary: "#5A3FC0",
  primarySoft: "#EDE7FF",
  text: "#2A1F4A",
  muted: "#7B7595",
  border: "#E8E1FA",
  danger: "#DC2A48",
  success: "#1A8A53",
  warn: "#D97706",
};

// ============== Shared helpers ==============
const fmt = (n) => (n || 0).toLocaleString("en-US", { maximumFractionDigits: 4 });
const fmtUSD = (n) => `$${fmt(n)}`;

function Section({ title, children, action }) {
  return (
    <section className="rounded-2xl border bg-white shadow-sm overflow-hidden"
             style={{ borderColor: ADMIN.border }}>
      <div className="flex items-center justify-between px-5 py-4 border-b"
           style={{ borderColor: ADMIN.border, background: ADMIN.primarySoft }}>
        <h2 className="font-serif text-lg" style={{ color: ADMIN.text }}>{title}</h2>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

function Btn({ children, variant = "primary", icon: Icon, onClick, disabled, testId, size = "md" }) {
  const styles = {
    primary: { bg: ADMIN.primary, color: "white" },
    soft: { bg: ADMIN.primarySoft, color: ADMIN.primary },
    danger: { bg: ADMIN.danger, color: "white" },
    ghost: { bg: "transparent", color: ADMIN.text, border: `1px solid ${ADMIN.border}` },
    success: { bg: ADMIN.success, color: "white" },
  }[variant] || {};
  const padding = size === "sm" ? "px-3 py-1.5 text-xs" : "px-4 py-2 text-sm";
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testId}
            className={`inline-flex items-center gap-2 rounded-lg ${padding} font-medium transition-all hover:opacity-90 disabled:opacity-50`}
            style={{ background: styles.bg, color: styles.color, border: styles.border || "none" }}>
      {Icon ? <Icon className="h-4 w-4" /> : null}
      {children}
    </button>
  );
}

function Stat({ label, value, sub, color = ADMIN.primary }) {
  return (
    <div className="rounded-xl border p-4" style={{ borderColor: ADMIN.border, background: "white" }}>
      <p className="text-[11px] uppercase tracking-[0.18em]" style={{ color: ADMIN.muted }}>{label}</p>
      <p className="font-serif text-2xl mt-1 tabular-nums" style={{ color }}>{value}</p>
      {sub ? <p className="text-xs mt-1" style={{ color: ADMIN.muted }}>{sub}</p> : null}
    </div>
  );
}

function Badge({ children, kind = "default" }) {
  const colors = {
    default: { bg: ADMIN.primarySoft, color: ADMIN.primary },
    success: { bg: "#E5F6EE", color: ADMIN.success },
    danger:  { bg: "#FCEAEE", color: ADMIN.danger },
    warn:    { bg: "#FEF3C7", color: ADMIN.warn },
    muted:   { bg: "#F1EEF7", color: ADMIN.muted },
  }[kind];
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.16em] font-medium"
          style={{ background: colors.bg, color: colors.color }}>
      {children}
    </span>
  );
}

// ============== ADMIN HOME ==============
export default function VIPAdmin() {
  return (
    <div data-testid="vip-admin-page" className="min-h-screen px-4 sm:px-8 py-6"
         style={{ background: ADMIN.bg }}>
      <header className="max-w-6xl mx-auto mb-6 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] mb-1" style={{ color: ADMIN.primary }}>
            Admin · Academia VIP
          </p>
          <h1 className="font-serif text-3xl" style={{ color: ADMIN.text }}>
            Pannello di gestione
          </h1>
        </div>
        <nav className="flex flex-wrap gap-1" data-testid="vip-admin-nav">
          {[
            { to: "/admin/vip", label: "Overview", icon: BookOpen, end: true },
            { to: "/admin/vip/lessons", label: "Lezioni", icon: GraduationCap },
            { to: "/admin/vip/videos", label: "Video", icon: Film },
            { to: "/admin/vip/payments", label: "Pagamenti", icon: CreditCard },
            { to: "/admin/vip/simulator", label: "Simulatore", icon: BarChart3 },
            { to: "/admin/vip/costs", label: "Costi IA", icon: Wallet },
            { to: "/admin/vip/users", label: "Utenti", icon: Crown },
            { to: "/admin/vip/subscribers", label: "Iscritti / Email", icon: Mail },
            { to: "/admin/vip/devices", label: "Dispositivi", icon: Smartphone },
            { to: "/admin/vip/funnel", label: "Funnel", icon: TrendingUp },
            { to: "/admin/vip/security-reviews", label: "Reclami sicurezza", icon: ShieldAlert },
            { to: "/admin/vip/migrate", label: "Migra", icon: Download },
          ].map((it) => (
            <NavLink
              key={it.to} to={it.to} end={it.end}
              data-testid={`admin-tab-${it.label.toLowerCase()}`}
              className={({ isActive }) =>
                `inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  isActive
                    ? "bg-white shadow-sm"
                    : "hover:bg-white/60"
                }`
              }
              style={({ isActive }) => ({
                color: isActive ? ADMIN.primary : ADMIN.muted,
                background: isActive ? "white" : "transparent",
                border: `1px solid ${ADMIN.border}`,
              })}
            >
              <it.icon className="h-4 w-4" /> {it.label}
            </NavLink>
          ))}
        </nav>
      </header>
      <div className="max-w-6xl mx-auto">
        <Outlet />
      </div>
    </div>
  );
}

// ============== Overview ==============
export function VIPAdminOverview() {
  const [data, setData] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(null);
  useEffect(() => {
    Promise.all([
      apiGet("/admin/vip/modules"),
      apiGet("/admin/vip/lessons"),
      apiGet("/admin/vip/costs").catch(() => null),
    ]).then(([modules, lessons, costs]) => {
      setData({ modules, lessons, costs });
    }).catch((e) => toast.error(e?.response?.data?.detail || "Errore caricamento"));
  }, []);

  async function downloadAcademyPDF(lang = "it") {
    setPdfBusy(lang);
    try {
      const res = await api.get("/admin/vip/academy/export-pdf", {
        params: { lang },
        responseType: "blob",
        timeout: 120000,
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.href = url;
      a.download = `mediquiz-academy-${lang}-${today}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF scaricato. Buon lavoro!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante l'export");
    } finally {
      setPdfBusy(null);
    }
  }

  if (!data) return <Loading />;

  const audioReady = data.lessons.filter((l) => l.audio_status === "ready").length;
  const published = data.lessons.filter((l) => l.published !== false).length;
  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Moduli" value={data.modules.length} sub="totali" />
        <Stat label="Lezioni" value={data.lessons.length} sub={`${published} pubblicate`} />
        <Stat label="Audio pronti" value={audioReady}
              sub={`${data.lessons.length - audioReady} da generare`} color={ADMIN.success} />
        <Stat label="Costo IA stimato"
              value={data.costs ? fmtUSD(data.costs.grand_total_estimate) : "—"}
              sub={data.costs ? `${data.costs.lesson_count} lezioni` : ""} />
      </div>

      <Section
        title="Esporta libro completo"
        action={null}
      >
        <div className="space-y-4 text-sm" style={{ color: ADMIN.text }}>
          <p>
            Tre versioni in PDF di <b>tutte le 105 lezioni</b> ordinate per modulo (M1 → M7),
            pronte per registrare gli audio con ElevenLabs e per la seconda edizione del libro.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-xl border p-4 flex flex-col gap-2"
                 style={{ borderColor: ADMIN.border, background: "#FAFAFF" }}>
              <p className="font-medium" style={{ color: ADMIN.text }}>🇮🇹 Italiano</p>
              <p className="text-xs flex-1" style={{ color: ADMIN.muted }}>
                Versione narrabile per ElevenLabs. Italiano simulazione esame.
              </p>
              <Btn
                icon={pdfBusy === "it" ? Loader2 : FileDown}
                variant="primary"
                size="sm"
                disabled={!!pdfBusy}
                onClick={() => downloadAcademyPDF("it")}
                testId="admin-export-academy-pdf-it"
              >
                {pdfBusy === "it" ? "Generazione…" : "Scarica IT"}
              </Btn>
            </div>

            <div className="rounded-xl border p-4 flex flex-col gap-2"
                 style={{ borderColor: ADMIN.border, background: "#FAFAFF" }}>
              <p className="font-medium" style={{ color: ADMIN.text }}>🇪🇸 Español</p>
              <p className="text-xs flex-1" style={{ color: ADMIN.muted }}>
                Solo español. Para revisión, traducciones o capítulos del libro en castellano.
              </p>
              <Btn
                icon={pdfBusy === "es" ? Loader2 : FileDown}
                variant="soft"
                size="sm"
                disabled={!!pdfBusy}
                onClick={() => downloadAcademyPDF("es")}
                testId="admin-export-academy-pdf-es"
              >
                {pdfBusy === "es" ? "Generazione…" : "Scarica ES"}
              </Btn>
            </div>

            <div className="rounded-xl border-2 p-4 flex flex-col gap-2"
                 style={{ borderColor: ADMIN.primary, background: ADMIN.primarySoft }}>
              <p className="font-medium flex items-center gap-1" style={{ color: ADMIN.primary }}>
                <Sparkles className="h-4 w-4" /> Bilingue (IT + ES)
              </p>
              <p className="text-xs flex-1" style={{ color: ADMIN.muted }}>
                Ogni lezione con IT prima e ES dopo. Ideale per la 2ª edizione del libro.
              </p>
              <Btn
                icon={pdfBusy === "bilingual" ? Loader2 : FileDown}
                variant="primary"
                size="sm"
                disabled={!!pdfBusy}
                onClick={() => downloadAcademyPDF("bilingual")}
                testId="admin-export-academy-pdf-bilingual"
              >
                {pdfBusy === "bilingual" ? "Generazione…" : "Scarica IT + ES"}
              </Btn>
            </div>
          </div>

          <p className="text-xs" style={{ color: ADMIN.muted }}>
            Ogni PDF include copertina, indice, sintesi, contenuto completo, slide e domande chiave.
            La generazione bilingue può richiedere 30–60 secondi. Solo per uso interno · drpabloterzano@gmail.com.
          </p>
        </div>
      </Section>

      <Section title="Moduli" action={
        <Link to="/admin/vip/lessons" className="text-sm inline-flex items-center gap-1"
              style={{ color: ADMIN.primary }}>
          Gestisci lezioni <ChevronRight className="h-3.5 w-3.5" />
        </Link>}>
        {data.modules.length === 0 ? (
          <p className="text-sm" style={{ color: ADMIN.muted }}>Nessun modulo creato. Vai a "Genera" per crearne uno.</p>
        ) : (
          <ul className="divide-y" style={{ borderColor: ADMIN.border }}>
            {data.modules.map((m) => (
              <li key={m.module_id} data-testid={`admin-module-row-${m.slug}`}
                  className="py-3 flex items-center justify-between">
                <div>
                  <p className="font-medium" style={{ color: ADMIN.text }}>{m.title_it}</p>
                  <p className="text-xs" style={{ color: ADMIN.muted }}>
                    {m.title_es} · {m.lesson_count || 0} lezioni
                  </p>
                </div>
                <Badge kind="muted">{m.cover}</Badge>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

function Loading() {
  return (
    <div className="py-20 flex justify-center" data-testid="admin-loading">
      <Loader2 className="h-6 w-6 animate-spin" style={{ color: ADMIN.primary }} />
    </div>
  );
}

// Hard-confirmation modal for destructive actions. The user must literally
// type the lesson title (or "ELIMINA") to enable the delete button. This
// prevents both accidental clicks AND muscle-memory clicks on bulk lists.
function DeleteConfirmModal({ open, onClose, onConfirm, lesson, busy }) {
  const [typed, setTyped] = useState("");
  useEffect(() => { if (open) setTyped(""); }, [open]);
  if (!open || !lesson) return null;
  const expected = "ELIMINA";
  const ok = typed.trim().toUpperCase() === expected;
  return (
    <div data-testid="admin-delete-modal"
         className="fixed inset-0 z-50 grid place-items-center p-4"
         style={{ background: "rgba(20,15,40,0.55)", backdropFilter: "blur(4px)" }}
         onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="rounded-2xl bg-white shadow-2xl max-w-md w-full p-6 border"
           style={{ borderColor: ADMIN.border }}>
        <div className="flex items-start gap-3 mb-4">
          <div className="shrink-0 h-10 w-10 rounded-full grid place-items-center"
               style={{ background: "#FCEAEE" }}>
            <AlertTriangle className="h-5 w-5" style={{ color: ADMIN.danger }} />
          </div>
          <div>
            <h3 className="font-serif text-xl" style={{ color: ADMIN.text }}>
              Eliminare lezione?
            </h3>
            <p className="text-sm mt-1" style={{ color: ADMIN.muted }}>
              Questa azione è <strong>irreversibile</strong>. Verranno eliminati anche gli audio
              e i progressi degli utenti su questa lezione.
            </p>
          </div>
        </div>
        <div className="rounded-lg border p-3 mb-4" style={{ borderColor: ADMIN.border, background: ADMIN.primarySoft }}>
          <p className="text-sm font-medium" style={{ color: ADMIN.text }}>{lesson.title_it}</p>
          <p className="text-xs mt-0.5" style={{ color: ADMIN.muted }}>
            ID: <span className="font-mono">{lesson.lesson_id}</span> · {lesson.title_es}
          </p>
        </div>
        <label className="block mb-4">
          <span className="block text-[11px] uppercase tracking-[0.16em] mb-1.5"
                style={{ color: ADMIN.muted }}>
            Per confermare, scrivi <strong style={{ color: ADMIN.danger }}>{expected}</strong>:
          </span>
          <input
            data-testid="admin-delete-confirm-input"
            value={typed} onChange={(e) => setTyped(e.target.value)}
            autoFocus
            placeholder={expected}
            className="w-full rounded-lg border px-3 py-2 text-sm font-mono"
            style={{ borderColor: ok ? ADMIN.danger : ADMIN.border, color: ADMIN.text, background: "white" }}
          />
        </label>
        <div className="flex gap-2 justify-end">
          <Btn variant="ghost" onClick={onClose} disabled={busy}
               testId="admin-delete-cancel">
            Annulla
          </Btn>
          <Btn variant="danger" icon={Trash2}
               disabled={!ok || busy}
               onClick={onConfirm}
               testId="admin-delete-confirm">
            {busy ? "Elimino…" : "Elimina definitivamente"}
          </Btn>
        </div>
      </div>
    </div>
  );
}

// ============== Lessons list & per-lesson actions ==============
export function VIPAdminLessons() {
  const [lessons, setLessons] = useState(null);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = () => apiGet("/admin/vip/lessons")
    .then(setLessons).catch((e) => toast.error(e?.response?.data?.detail || "Errore"));
  useEffect(() => { load(); }, []);

  const togglePublish = async (l) => {
    setBusy(l.lesson_id);
    try {
      await apiPost(`/admin/vip/lessons/${l.lesson_id}/publish?published=${l.published === false}`, {});
      toast.success(l.published === false ? "Lezione pubblicata" : "Lezione nascosta");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    setBusy(null);
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setBusy(toDelete.lesson_id);
    try {
      await apiDelete(`/admin/vip/lessons/${toDelete.lesson_id}`);
      toast.success("Lezione eliminata");
      setToDelete(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    setBusy(null);
  };

  const regenAudio = async (l, langs) => {
    setBusy(l.lesson_id);
    try {
      await apiPost(`/admin/vip/lessons/${l.lesson_id}/audio/regenerate`, { languages: langs });
      toast.success(`Rigenerazione audio in corso (${langs.join(", ")})…`);
      // Poll status every 5s up to 60s
      for (let i = 0; i < 12; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        const fresh = await apiGet(`/admin/vip/lessons/${l.lesson_id}/status`);
        if (fresh.audio_status === "ready") { toast.success("Audio rigenerato!"); load(); break; }
        if (fresh.audio_status === "error") { toast.error(`Errore: ${fresh.audio_error || "?"}`); break; }
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    setBusy(null);
  };

  if (!lessons) return <Loading />;
  const filtered = lessons.filter((l) =>
    !filter || (l.title_it || "").toLowerCase().includes(filter.toLowerCase())
    || (l.title_es || "").toLowerCase().includes(filter.toLowerCase())
  );
  return (
    <>
    <Section title={`Lezioni (${lessons.length})`} action={
      <div className="relative">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2"
                style={{ color: ADMIN.muted }} />
        <input data-testid="admin-search"
               value={filter} onChange={(e) => setFilter(e.target.value)}
               placeholder="Cerca…"
               className="pl-9 pr-3 py-1.5 rounded-lg border text-sm"
               style={{ borderColor: ADMIN.border, color: ADMIN.text, background: "white" }} />
      </div>
    }>
      {filtered.length === 0 ? (
        <p className="text-sm py-8 text-center" style={{ color: ADMIN.muted }}>
          Nessuna lezione corrisponde.
        </p>
      ) : (
        <div className="-mx-5 overflow-x-auto">
          <table className="w-full text-sm">
            <thead style={{ background: ADMIN.primarySoft, color: ADMIN.muted }}>
              <tr className="text-left">
                <th className="px-5 py-2 font-medium text-[11px] uppercase tracking-wider">Titolo (IT/ES)</th>
                <th className="px-3 py-2 font-medium text-[11px] uppercase tracking-wider">Stato</th>
                <th className="px-3 py-2 font-medium text-[11px] uppercase tracking-wider">Audio</th>
                <th className="px-5 py-2 font-medium text-[11px] uppercase tracking-wider text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => (
                <tr key={l.lesson_id} data-testid={`admin-lesson-row-${l.lesson_id}`}
                    className="border-t" style={{ borderColor: ADMIN.border }}>
                  <td className="px-5 py-3">
                    <p style={{ color: ADMIN.text }}>{l.title_it}</p>
                    <p className="text-xs mt-0.5" style={{ color: ADMIN.muted }}>{l.title_es}</p>
                  </td>
                  <td className="px-3 py-3">
                    {l.published === false
                      ? <Badge kind="warn">Bozza</Badge>
                      : <Badge kind="success">Pubblicata</Badge>}
                  </td>
                  <td className="px-3 py-3">
                    {l.audio_status === "ready" ? <Badge kind="success">Pronto</Badge>
                      : l.audio_status === "generating" ? <Badge kind="warn">In corso</Badge>
                      : l.audio_status === "error" ? <Badge kind="danger">Errore</Badge>
                      : <Badge kind="muted">Mancante</Badge>}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex gap-1.5">
                      <Link to={`/admin/vip/lessons/${l.lesson_id}`}
                            data-testid={`admin-lesson-edit-${l.lesson_id}`}
                            className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md"
                            style={{ background: ADMIN.primarySoft, color: ADMIN.primary }}>
                        <FileText className="h-3.5 w-3.5" /> Modifica
                      </Link>
                      <Btn size="sm" variant="ghost"
                           icon={l.published === false ? Eye : EyeOff}
                           disabled={busy === l.lesson_id}
                           onClick={() => togglePublish(l)}
                           testId={`admin-lesson-publish-${l.lesson_id}`}>
                        {l.published === false ? "Pubblica" : "Nascondi"}
                      </Btn>
                      <Btn size="sm" variant="ghost" icon={Music}
                           disabled={busy === l.lesson_id}
                           onClick={() => regenAudio(l, ["it","es"])}
                           testId={`admin-lesson-regen-${l.lesson_id}`}>
                        Rigenera audio
                      </Btn>
                      <Btn size="sm" variant="ghost" icon={Trash2}
                           disabled={busy === l.lesson_id}
                           onClick={() => setToDelete(l)}
                           testId={`admin-lesson-delete-${l.lesson_id}`}>
                        Elimina
                      </Btn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
    <DeleteConfirmModal
      open={!!toDelete}
      lesson={toDelete}
      busy={busy === toDelete?.lesson_id}
      onClose={() => setToDelete(null)}
      onConfirm={confirmDelete}
    />
    </>
  );
}

// ============== Lesson editor ==============
export function VIPAdminLessonEdit() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiGet(`/admin/vip/lessons/${lessonId}`).then(setLesson)
      .catch((e) => toast.error(e?.response?.data?.detail || "Errore"));
  }, [lessonId]);

  if (!lesson) return <Loading />;

  const update = (k, v) => setDirty((d) => ({ ...d, [k]: v }));
  const get = (k) => (k in dirty ? dirty[k] : lesson[k]);
  const isDirty = Object.keys(dirty).length > 0;

  const save = async () => {
    setSaving(true);
    try {
      await apiPatch(`/admin/vip/lessons/${lessonId}`, dirty);
      toast.success("Modifiche salvate");
      const fresh = await apiGet(`/admin/vip/lessons/${lessonId}`);
      setLesson(fresh); setDirty({});
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore salvataggio"); }
    setSaving(false);
  };

  // Slide editor: editing slides_it/es means replacing the full array.
  const updateSlide = (lang, i, key, value) => {
    const arr = [...(get(`slides_${lang}`) || [])];
    arr[i] = { ...arr[i], [key]: value };
    update(`slides_${lang}`, arr);
  };
  const updateSlideBullet = (lang, i, j, value) => {
    const arr = [...(get(`slides_${lang}`) || [])];
    const bullets = [...(arr[i].bullets || [])];
    bullets[j] = value;
    arr[i] = { ...arr[i], bullets };
    update(`slides_${lang}`, arr);
  };

  return (
    <div className="space-y-5" data-testid="admin-lesson-editor">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Link to="/admin/vip/lessons"
              className="inline-flex items-center gap-1.5 text-sm font-medium"
              style={{ color: ADMIN.primary }}>
          <ArrowLeft className="h-4 w-4" /> Torna alle lezioni
        </Link>
        <div className="flex gap-2">
          {isDirty && (
            <span className="text-xs flex items-center gap-1.5" style={{ color: ADMIN.warn }}>
              <AlertCircle className="h-4 w-4" /> Modifiche non salvate
            </span>
          )}
          <Btn icon={Save} variant="primary" disabled={!isDirty || saving}
               onClick={save} testId="admin-lesson-save">
            {saving ? "Salvo…" : "Salva modifiche"}
          </Btn>
        </div>
      </div>

      <Section title="Titoli e sintesi">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Titolo (IT)" value={get("title_it") || ""}
                 onChange={(v) => update("title_it", v)} testId="admin-edit-title-it" />
          <Field label="Título (ES)" value={get("title_es") || ""}
                 onChange={(v) => update("title_es", v)} testId="admin-edit-title-es" />
          <Field label="Sintesi (IT)" value={get("summary_it") || ""}
                 onChange={(v) => update("summary_it", v)} multiline />
          <Field label="Resumen (ES)" value={get("summary_es") || ""}
                 onChange={(v) => update("summary_es", v)} multiline />
        </div>
      </Section>

      <Section title="Corpo della lezione">
        <div className="grid lg:grid-cols-2 gap-4">
          <Field label="Testo IT" value={get("body_it") || ""}
                 onChange={(v) => update("body_it", v)} multiline rows={14} />
          <Field label="Texto ES" value={get("body_es") || ""}
                 onChange={(v) => update("body_es", v)} multiline rows={14} />
        </div>
      </Section>

      {["it","es"].map((lang) => {
        const slides = get(`slides_${lang}`) || [];
        return (
          <Section key={lang} title={`Slides ${lang.toUpperCase()} (${slides.length})`}>
            <div className="space-y-4">
              {slides.map((s, i) => (
                <div key={i} className="rounded-lg border p-3"
                     style={{ borderColor: ADMIN.border }}
                     data-testid={`admin-slide-${lang}-${i}`}>
                  <Field label={`Slide ${i+1} · titolo`} value={s.title || ""}
                         onChange={(v) => updateSlide(lang, i, "title", v)} />
                  <div className="mt-2 space-y-1.5">
                    {(s.bullets || []).map((b, j) => (
                      <Field key={j} label="" small value={b}
                             onChange={(v) => updateSlideBullet(lang, i, j, v)} />
                    ))}
                  </div>
                </div>
              ))}
              {slides.length === 0 ? (
                <p className="text-sm" style={{ color: ADMIN.muted }}>Nessuna slide.</p>
              ) : null}
            </div>
          </Section>
        );
      })}

      <ImagesPanel lessonId={lessonId} lesson={lesson} onRefresh={async () => {
        const fresh = await apiGet(`/admin/vip/lessons/${lessonId}`);
        setLesson(fresh);
      }} />

      <VideoPanel lessonId={lessonId} lesson={lesson} onRefresh={async () => {
        const fresh = await apiGet(`/admin/vip/lessons/${lessonId}`);
        setLesson(fresh);
      }} />

      <Section title={`Domande (${(get("questions") || []).length})`}>
        <div className="space-y-3">
          {(get("questions") || []).map((q, qi) => (
            <div key={qi} className="rounded-lg border p-3"
                 style={{ borderColor: ADMIN.border }}
                 data-testid={`admin-question-${qi}`}>
              <Field label={`Q${qi+1} (IT)`} value={q.stem_it || ""}
                     onChange={(v) => {
                       const arr = [...(get("questions") || [])];
                       arr[qi] = { ...arr[qi], stem_it: v };
                       update("questions", arr);
                     }} />
              <div className="mt-2 grid grid-cols-2 gap-1.5">
                {(q.options_it || []).map((opt, oi) => (
                  <Field key={oi} small value={opt}
                         label={oi === q.correct_index ? `${String.fromCharCode(65+oi)} ✓` : String.fromCharCode(65+oi)}
                         onChange={(v) => {
                           const arr = [...(get("questions") || [])];
                           const opts = [...(arr[qi].options_it || [])];
                           opts[oi] = v;
                           arr[qi] = { ...arr[qi], options_it: opts };
                           update("questions", arr);
                         }} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Pubblicazione, free preview & audio">
        <div className="flex flex-wrap gap-3 items-center">
          <Btn variant={get("published") === false ? "soft" : "success"}
               icon={get("published") === false ? Eye : CheckCircle2}
               onClick={() => update("published", get("published") === false ? true : false)}>
            {get("published") === false ? "Marca come Pubblicata" : "Marca come Bozza"}
          </Btn>
          <FreePreviewToggle lessonId={lessonId}
                              isFreePreview={!!lesson.is_free_preview}
                              onChanged={async () => {
                                const fresh = await apiGet(`/admin/vip/lessons/${lessonId}`);
                                setLesson(fresh);
                              }} />
          <span className="text-xs px-2 py-1 rounded-md self-center"
                style={{ background: ADMIN.primarySoft, color: ADMIN.muted }}>
            Audio status: <strong>{lesson.audio_status || "—"}</strong>
            {lesson.audio_path_it ? " · IT ✓" : ""} {lesson.audio_path_es ? " · ES ✓" : ""}
          </span>
        </div>
        {/* Manual audio upload — Phase 5: avoid spending OpenAI TTS credits.
            Admin uploads mp3 / wav / m4a (max 80 MB) per language. The
            "Rigenera audio" AI flow stays available on the lessons list
            for operators who still want auto-generated audio. */}
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <AudioLangUpload lessonId={lessonId} lang="it"
                            existingPath={lesson.audio_path_it}
                            existingBytes={lesson.audio_bytes_it}
                            onChanged={async () => {
                              const fresh = await apiGet(`/admin/vip/lessons/${lessonId}`);
                              setLesson(fresh);
                            }} />
          <AudioLangUpload lessonId={lessonId} lang="es"
                            existingPath={lesson.audio_path_es}
                            existingBytes={lesson.audio_bytes_es}
                            onChanged={async () => {
                              const fresh = await apiGet(`/admin/vip/lessons/${lessonId}`);
                              setLesson(fresh);
                            }} />
        </div>
      </Section>
    </div>
  );
}

// ============== Manual audio upload (per language) ==============
//
// Phase 5: replaces (or supplements) the OpenAI-TTS flow with manual
// uploads. Accepts mp3 / wav / m4a up to 80 MB. Progress bar via XHR
// so the operator sees real bytes-on-the-wire, not just spinner.
export function AudioLangUpload({ lessonId, lang, existingPath, existingBytes, onChanged }) {
  const fileInputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(0);
  const accept = ".mp3,.wav,.m4a,audio/mpeg,audio/wav,audio/mp4,audio/x-m4a";
  const langLabel = lang === "it" ? "Italiano" : "Español";

  const upload = (file) => {
    if (!file) return;
    if (file.size > 80 * 1024 * 1024) {
      toast.error("Il file supera 80 MB"); return;
    }
    setBusy(true); setPct(0);
    const xhr = new XMLHttpRequest();
    const url = `${API}/admin/vip/lessons/${lessonId}/audio/upload?lang=${lang}`;
    xhr.open("POST", url, true);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) {
        setPct(Math.round((ev.loaded / ev.total) * 100));
      }
    };
    xhr.onload = async () => {
      setBusy(false); setPct(0);
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (xhr.status >= 200 && xhr.status < 300) {
        toast.success(`Audio ${langLabel} caricato`);
        await onChanged?.();
      } else {
        let msg = `HTTP ${xhr.status}`;
        try { msg = JSON.parse(xhr.responseText).detail || msg; } catch {  }
        toast.error(`Errore upload: ${msg}`);
      }
    };
    xhr.onerror = () => {
      setBusy(false); setPct(0);
      toast.error("Errore di rete durante l'upload");
    };
    const fd = new FormData();
    fd.append("file", file);
    xhr.send(fd);
  };

  const remove = async () => {
    if (!window.confirm(`Eliminare l'audio ${langLabel}?`)) return;
    setBusy(true);
    try {
      await apiDelete(`/admin/vip/lessons/${lessonId}/audio/${lang}`);
      toast.success(`Audio ${langLabel} eliminato`);
      await onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
    setBusy(false);
  };

  const sizeMb = existingBytes ? (existingBytes / 1024 / 1024).toFixed(1) : null;
  return (
    <div className="rounded-lg border p-3 space-y-2"
         style={{ borderColor: ADMIN.border, background: "white" }}
         data-testid={`admin-audio-upload-${lang}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-sm font-semibold" style={{ color: ADMIN.text }}>
          Audio {langLabel}
        </span>
        {existingPath
          ? <Badge kind="success">Caricato{sizeMb ? ` · ${sizeMb} MB` : ""}</Badge>
          : <Badge kind="muted">Nessuno</Badge>}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        disabled={busy}
        onChange={(e) => upload(e.target.files?.[0])}
        data-testid={`admin-audio-input-${lang}`}
        className="block w-full text-xs"
      />
      {busy && (
        <div className="text-xs" style={{ color: ADMIN.muted }}>
          Caricamento {pct}%
          <div className="h-1.5 rounded-full mt-1 overflow-hidden"
               style={{ background: ADMIN.primarySoft }}>
            <div className="h-full transition-[width] duration-150"
                 style={{ width: `${pct}%`, background: ADMIN.primary }} />
          </div>
        </div>
      )}
      {existingPath && !busy && (
        <button
          onClick={remove}
          data-testid={`admin-audio-delete-${lang}`}
          className="text-xs underline"
          style={{ color: "#9E2A48" }}
        >
          Elimina audio
        </button>
      )}
      <p className="text-[11px]" style={{ color: ADMIN.muted }}>
        mp3 · wav · m4a · max 80 MB
      </p>
    </div>
  );
}


// ============== Free preview toggle ==============
//
// Backend: POST /api/admin/vip/lessons/{id}/free-preview {enabled: bool}.
// Lesson with is_free_preview=true is unlocked for ALL authenticated users
// regardless of academy purchase. Used as the funnel teaser (M1.L1).
export function FreePreviewToggle({ lessonId, isFreePreview, onChanged }) {
  const [busy, setBusy] = useState(false);
  const enabled = !!isFreePreview;
  const toggle = async () => {
    setBusy(true);
    try {
      await apiPost(`/admin/vip/lessons/${lessonId}/free-preview`,
                    { enabled: !enabled });
      toast.success(enabled ? "Free preview disabilitato" : "Lezione marcata come gratuita");
      await onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
    setBusy(false);
  };
  return (
    <Btn variant={enabled ? "success" : "soft"}
         icon={Gift}
         disabled={busy}
         onClick={toggle}
         testId="admin-free-preview-toggle">
      {busy ? "…" : enabled ? "Free preview ATTIVO" : "Marca come Free preview"}
    </Btn>
  );
}


// ============== Images panel ==============
//
// Read-only grid of currently stored lesson images + per-slot regeneration
// button. Backend regenerate endpoint accepts a slot-filtered subset of
// `image_prompts`, so we send only the prompt for the slot the admin clicks.
// Polling reuses /images/status (already used by VIPAdminGenerate).
export function ImagesPanel({ lessonId, lesson, onRefresh }) {
  const [busySlot, setBusySlot] = useState(null);
  const images = lesson.images || [];
  const prompts = lesson.image_prompts || [];

  const regenerateSlot = async (slot) => {
    const prompt = prompts.find((p) => p.slot === slot);
    if (!prompt) {
      toast.error(`Nessun prompt salvato per ${slot}`);
      return;
    }
    if (!window.confirm(`Rigenerare l'immagine ${slot}? L'immagine attuale verrà sostituita.`))
      return;
    setBusySlot(slot);
    try {
      await apiPost(`/admin/vip/lessons/${lessonId}/images/regenerate`, {
        image_prompts: [prompt],
      });
      toast.success(`Rigenerazione ${slot} avviata…`);
      // Poll status every 4s up to 90s
      for (let i = 0; i < 23; i++) {
        await new Promise((r) => setTimeout(r, 4000));
        const s = await apiGet(`/admin/vip/lessons/${lessonId}/images/status`);
        if (s.images_status === "ready") {
          toast.success("Immagine rigenerata");
          await onRefresh?.();
          break;
        }
        if (s.images_status === "error") {
          toast.error(`Errore: ${s.images_error || "?"}`);
          break;
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
    setBusySlot(null);
  };

  return (
    <Section title={`Immagini (${images.length})`} action={
      images.length === 0 && prompts.length > 0 ? (
        <span className="text-xs" style={{ color: ADMIN.muted }}>
          {prompts.length} prompt salvati · genera dalla pagina "Genera"
        </span>
      ) : null
    }>
      {images.length === 0 ? (
        <p className="text-sm py-6 text-center" style={{ color: ADMIN.muted }}>
          Nessuna immagine salvata.
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {images.map((img) => (
            <div key={img.slot}
                 className="rounded-xl border overflow-hidden flex flex-col"
                 style={{ borderColor: ADMIN.border, background: ADMIN.card }}
                 data-testid={`admin-image-${img.slot}`}>
              <div className="aspect-video flex items-center justify-center"
                   style={{ background: "#0F0F1A" }}>
                {img.url_path ? (
                  <img src={`${API}${img.url_path}`}
                       alt={img.alt_it || img.alt || img.slot}
                       className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon className="h-8 w-8" style={{ color: ADMIN.muted }} />
                )}
              </div>
              <div className="p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-mono uppercase tracking-wider"
                        style={{ color: ADMIN.muted }}>{img.slot}</span>
                  {img.image_slot_for_slide != null ? (
                    <Badge>slide {img.image_slot_for_slide + 1}</Badge>
                  ) : null}
                </div>
                {img.alt_it ? (
                  <p className="text-xs leading-relaxed line-clamp-3"
                     style={{ color: ADMIN.text }} title={img.alt_it}>
                    <span style={{ color: ADMIN.muted }}>IT:</span> {img.alt_it}
                  </p>
                ) : null}
                {img.alt_es ? (
                  <p className="text-xs leading-relaxed line-clamp-2"
                     style={{ color: ADMIN.muted }} title={img.alt_es}>
                    <span>ES:</span> {img.alt_es}
                  </p>
                ) : null}
                <Btn size="sm" variant="ghost" icon={Wand2}
                     disabled={busySlot === img.slot || !prompts.find((p) => p.slot === img.slot)}
                     onClick={() => regenerateSlot(img.slot)}
                     testId={`admin-image-regen-${img.slot}`}>
                  {busySlot === img.slot ? "Rigenero…" : "Rigenera"}
                </Btn>
              </div>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}


// ============== Video panel — bilingual IT/ES ==============
//
// Each lesson can have an independent video per language (IT / ES). The
// panel exposes 2 tabs (one per lang) with: external URL input (YouTube /
// Vimeo / direct .mp4) AND file upload up to ~500 MB. Each language has
// its own published toggle. The student player only renders a video if the
// user's UI language matches a published video.
const VIDEO_MAX_BYTES = 500 * 1024 * 1024;
const VIDEO_MAX_MB = VIDEO_MAX_BYTES / (1024 * 1024);

function VideoLangPanel({ lang, lessonId, video, onRefresh }) {
  const [url, setUrl] = useState(video?.kind === "url" ? (video.url || "") : "");
  const [busy, setBusy] = useState(null); // 'save'|'upload'|'publish'|'delete'|null
  const [uploadPct, setUploadPct] = useState(0);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setUrl(video?.kind === "url" ? (video.url || "") : "");
  }, [video?.kind, video?.url]);

  const saveUrl = async () => {
    if (!url.trim()) {
      toast.error("Inserisci una URL");
      return;
    }
    setBusy("save");
    try {
      await apiPatch(`/admin/vip/lessons/${lessonId}/video`, {
        lang, url: url.trim(),
      });
      toast.success(`URL salvata · ${lang.toUpperCase()}`);
      await onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
    setBusy(null);
  };

  const togglePublish = async () => {
    // Safety net: if the admin tries to publish an upload that wasn't
    // auto-optimized (ffmpeg missing on server at upload time), ask for
    // explicit confirmation — otherwise students will hit infinite
    // "Loading video…" on first play.
    if (!video?.published
        && video?.kind === "upload"
        && video?.faststart === false) {
      const proceed = window.confirm(
        "⚠️ Questo video NON è ottimizzato per il web (moov atom alla fine del file).\n\n" +
        "Gli studenti vedranno un caricamento infinito al primo play di video grandi.\n\n" +
        "Soluzione consigliata: clicca prima 'Riparare' qui sotto, oppure ricarica il " +
        "file dopo averlo esportato con HandBrake → Web Optimized ✓.\n\n" +
        "Pubblicare comunque?"
      );
      if (!proceed) return;
    }
    setBusy("publish");
    try {
      await apiPatch(`/admin/vip/lessons/${lessonId}/video`, {
        lang, published: !video?.published,
      });
      toast.success(video?.published ? "Video nascosto" : "Video pubblicato");
      await onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
    setBusy(null);
  };

  const removeVideo = async () => {
    if (!window.confirm(`Rimuovere il video ${lang.toUpperCase()}?`)) return;
    setBusy("delete");
    try {
      await apiDelete(`/admin/vip/lessons/${lessonId}/video/${lang}`);
      toast.success("Video rimosso");
      setUrl("");
      await onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
    setBusy(null);
  };

  // Track the in-flight upload session so we can cancel cleanly when the
  // user closes the tab or aborts. Stored in a ref so React re-renders
  // don't kick off duplicate xhr's.
  const abortRef = useRef({ aborted: false });

  // Chunked upload. The frontend splits the file into ~20 MB pieces and
  // POSTs each individually, so the production Kubernetes ingress (which
  // typically caps single-request body size around 25 MB) never sees a
  // body big enough to reject. Each chunk uses an XHR so we get real
  // upload-progress events for the UI.
  //
  // Resilience: each chunk has up to 3 retries with exponential backoff
  // (1s → 2s → 4s). If all retries fail, the partial session is cancelled
  // server-side so no orphan tmp dir is left behind.
  const upload = async (file) => {
    if (!file) return;
    if (file.size > VIDEO_MAX_BYTES) {
      toast.error(`Il file supera il limite massimo consentito di ${VIDEO_MAX_MB} MB.`);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setBusy("upload");
    setUploadPct(0);
    abortRef.current = { aborted: false };
    let uploadId = null;
    // 1. INIT — negotiate upload_id + chunk_size
    let init;
    try {
      // We assume 20 MB chunks; the server may reply with a different
      // chunk_size (env-tunable). Use whatever the server returns.
      const PROPOSED_CHUNK = 20 * 1024 * 1024;
      const proposedChunks = Math.max(1, Math.ceil(file.size / PROPOSED_CHUNK));
      init = await apiPost(
        `/admin/vip/lessons/${lessonId}/video/upload/init`,
        {
          lang,
          filename: file.name || `video.mp4`,
          total_size: file.size,
          content_type: file.type || "video/mp4",
          total_chunks: proposedChunks,
        }
      );
      uploadId = init.upload_id;
    } catch (e) {
      setBusy(null); setUploadPct(0);
      if (fileInputRef.current) fileInputRef.current.value = "";
      toast.error(e?.response?.data?.detail || "Errore inizializzando l'upload");
      return;
    }
    const chunkSize = init.chunk_size;
    const totalChunks = init.total_chunks;
    // If the negotiated chunk_size differs from our proposal, recompute
    // the split — we never want to send more chunks than registered.
    const realChunks = Math.max(1, Math.ceil(file.size / chunkSize));
    if (realChunks !== totalChunks) {
      // Renegotiate gracefully by re-initialising with the right count.
      try {
        await apiDelete(
          `/admin/vip/lessons/${lessonId}/video/upload/${uploadId}`
        );
        init = await apiPost(
          `/admin/vip/lessons/${lessonId}/video/upload/init`,
          {
            lang,
            filename: file.name || `video.mp4`,
            total_size: file.size,
            content_type: file.type || "video/mp4",
            total_chunks: realChunks,
          }
        );
        uploadId = init.upload_id;
      } catch (e) {
        setBusy(null); setUploadPct(0);
        if (fileInputRef.current) fileInputRef.current.value = "";
        toast.error("Impossibile rinegoziare la dimensione del chunk");
        return;
      }
    }
    const finalChunkCount = init.total_chunks;

    // 2. UPLOAD CHUNKS — sequential to keep memory & order predictable.
    let bytesUploaded = 0;
    const sendChunk = (idx) => new Promise((resolve, reject) => {
      const start = idx * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const blob = file.slice(start, end);
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${API}/admin/vip/lessons/${lessonId}/video/upload/${uploadId}/chunk/${idx}`, true);
      xhr.withCredentials = true;
      xhr.setRequestHeader("Content-Type", "application/octet-stream");
      // Per-chunk progress (smooths the bar even for chunks that take seconds)
      xhr.upload.onprogress = (ev) => {
        if (!ev.lengthComputable) return;
        const totalSoFar = bytesUploaded + ev.loaded;
        setUploadPct(Math.min(99, Math.round((totalSoFar / file.size) * 100)));
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          bytesUploaded += blob.size;
          setUploadPct(Math.min(99, Math.round((bytesUploaded / file.size) * 100)));
          resolve();
        } else {
          let msg = `HTTP ${xhr.status}`;
          try { msg = JSON.parse(xhr.responseText).detail || msg; } catch { /* keep */ }
          reject(new Error(msg));
        }
      };
      xhr.onerror = () => reject(new Error("network"));
      xhr.onabort = () => reject(new Error("aborted"));
      xhr.send(blob);
    });

    const trySend = async (idx) => {
      let lastErr;
      for (let attempt = 0; attempt < 3; attempt++) {
        if (abortRef.current.aborted) throw new Error("aborted");
        try {
          await sendChunk(idx);
          return;
        } catch (err) {
          lastErr = err;
          if (err.message === "aborted") throw err;
          // Exponential backoff: 1s, 2s, 4s
          await new Promise((r) => setTimeout(r, 1000 * Math.pow(2, attempt)));
        }
      }
      throw lastErr || new Error("chunk failed");
    };

    try {
      for (let i = 0; i < finalChunkCount; i++) {
        await trySend(i);
      }
    } catch (e) {
      // Cleanup the abandoned session server-side so no orphan tmp dir remains.
      try {
        await apiDelete(
          `/admin/vip/lessons/${lessonId}/video/upload/${uploadId}`
        );
      } catch { /* ignore */ }
      setBusy(null); setUploadPct(0);
      if (fileInputRef.current) fileInputRef.current.value = "";
      toast.error(e.message === "aborted"
        ? "Upload annullato"
        : `Errore durante il chunk: ${e.message}`);
      return;
    }

    // 3. FINALIZE — backend concatenates chunks and pushes to Object Store.
    setUploadPct(100);  // bytes done; server still has to assemble + S3 push.
    try {
      await apiPost(
        `/admin/vip/lessons/${lessonId}/video/upload/${uploadId}/finalize`,
        {}
      );
      toast.success(`Video caricato (${(file.size / 1024 / 1024).toFixed(1)} MB)`);
      await onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante la finalizzazione");
    } finally {
      setBusy(null); setUploadPct(0);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const status = !video ? "Nessun video" :
    video.kind === "url" ? `URL esterna · ${video.published ? "Pubblicato" : "Bozza · solo admin"}` :
    `File caricato (${video.size_mb || "?"} MB) · ${video.published ? "Pubblicato" : "Bozza · solo admin"}`;

  // Build a stable preview URL. URL-kind: use embed_url (YouTube/Vimeo) or
  // the raw url as a fallback for .mp4 links. Upload-kind: stream via the
  // backend, with a cache-busting param tied to uploaded_at so updates show.
  const previewSrc = (() => {
    if (!video) return null;
    if (video.kind === "url") {
      return video.embed_url || video.url || null;
    }
    return `${API}/vip/lessons/${lessonId}/video/${lang}?v=${encodeURIComponent(video.uploaded_at || "")}`;
  })();
  const isIframe = video?.kind === "url" && !!video?.embed_url;
  const isDirectVideoTag = video && (video.kind === "upload"
    || (video.kind === "url" && !video.embed_url && /\.(mp4|webm|mov|m4v)(\?|$)/i.test(video.url || "")));

  return (
    <div className="rounded-lg border p-4 space-y-3"
         style={{ borderColor: ADMIN.border, background: ADMIN.card }}
         data-testid={`admin-video-${lang}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="font-medium text-sm" style={{ color: ADMIN.text }}>
          Video {lang.toUpperCase()}
        </span>
        <Badge kind={video?.published ? "success" : "default"}>{status}</Badge>
      </div>

      {/* Inline preview — admin sees the video immediately after save/upload,
          regardless of publish state. Confirms the URL is valid + reachable. */}
      {previewSrc ? (
        <div className="rounded-md overflow-hidden border bg-black"
             style={{ borderColor: ADMIN.border }}
             data-testid={`admin-video-${lang}-preview`}>
          <div className="aspect-video w-full">
            {isIframe ? (
              <iframe src={previewSrc}
                      title={`Anteprima video ${lang.toUpperCase()}`}
                      className="w-full h-full"
                      allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                      allowFullScreen
                      data-testid={`admin-video-${lang}-iframe`} />
            ) : isDirectVideoTag ? (
              <video src={previewSrc} controls preload="metadata"
                     className="w-full h-full"
                     data-testid={`admin-video-${lang}-video`} />
            ) : (
              <div className="w-full h-full grid place-items-center text-white/80 text-xs px-4 text-center">
                Anteprima non incorporabile. <a href={previewSrc} target="_blank"
                rel="noopener noreferrer" className="underline ml-1">Apri in una nuova scheda</a>
              </div>
            )}
          </div>
        </div>
      ) : null}

      <div className="space-y-2">
        <label className="text-[11px] uppercase tracking-wider"
               style={{ color: ADMIN.muted }}>URL esterna (YouTube / Vimeo / .mp4)</label>
        <div className="flex gap-2">
          <input type="url"
                 placeholder="https://youtu.be/… o https://vimeo.com/…"
                 value={url}
                 disabled={video?.kind === "upload"}
                 onChange={(e) => setUrl(e.target.value)}
                 data-testid={`admin-video-${lang}-url-input`}
                 className="flex-1 rounded-md border px-3 py-2 text-sm"
                 style={{ borderColor: ADMIN.border, background: video?.kind === "upload" ? "#F6F6FA" : "white" }} />
          <Btn variant="primary" icon={LinkIcon}
               disabled={busy !== null || video?.kind === "upload"}
               onClick={saveUrl}
               testId={`admin-video-${lang}-save`}>
            {busy === "save" ? "…" : "Salva URL"}
          </Btn>
        </div>
        {video?.kind === "upload" ? (
          <p className="text-[11px]" style={{ color: ADMIN.muted }}>
            Per usare una URL esterna, prima rimuovi il file caricato.
          </p>
        ) : null}
      </div>

      <div className="flex items-center gap-2">
        <span className="text-[11px] flex-1" style={{ color: ADMIN.muted }}>oppure</span>
        <input type="file" accept="video/*"
               ref={fileInputRef}
               id={`admin-video-${lang}-file`}
               data-testid={`admin-video-${lang}-file`}
               onChange={(e) => upload(e.target.files?.[0])}
               className="hidden"
               disabled={busy !== null} />
        <label htmlFor={`admin-video-${lang}-file`}
               className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-medium transition-colors hover:bg-[#F6F6FA]"
               style={{ borderColor: ADMIN.border, color: ADMIN.text,
                        opacity: busy && busy !== "upload" ? 0.5 : 1,
                        pointerEvents: busy ? "none" : "auto" }}
               data-testid={`admin-video-${lang}-upload-trigger`}>
          <Upload className="h-3.5 w-3.5" />
          {busy === "upload"
            ? (uploadPct >= 100 ? "Salvataggio…" : `Caricamento… ${uploadPct}%`)
            : `Carica file (max ${VIDEO_MAX_MB} MB)`}
        </label>
      </div>

      {/* Upload progress bar (only visible during upload) */}
      {busy === "upload" ? (
        <div className="w-full h-1.5 rounded-full overflow-hidden"
             style={{ background: "#E8E5F0" }}
             data-testid={`admin-video-${lang}-upload-progress`}>
          <div className="h-full transition-[width] duration-150"
               style={{ width: `${uploadPct}%`, background: "#5A3FC0" }} />
        </div>
      ) : null}

      {video ? (
        <div className="flex gap-2 pt-2 border-t flex-wrap" style={{ borderColor: ADMIN.border }}>
          <Btn variant={video.published ? "soft" : "success"}
               icon={video.published ? EyeOff : Eye}
               size="sm"
               disabled={busy !== null}
               onClick={togglePublish}
               testId={`admin-video-${lang}-publish-toggle`}>
            {busy === "publish" ? "…" : video.published ? "Nascondi" : "Pubblica"}
          </Btn>
          <Btn variant="danger" icon={Trash2} size="sm"
               disabled={busy !== null}
               onClick={removeVideo}
               testId={`admin-video-${lang}-delete`}>
            Rimuovi
          </Btn>
          {video.kind === "upload" ? (
            <VideoFaststartTool lessonId={lessonId} lang={lang}
                                faststartField={video.faststart}
                                onChanged={onRefresh} />
          ) : null}
          {video.kind === "url" && video.embed_url ? (
            <a href={video.embed_url} target="_blank" rel="noopener noreferrer"
               className="text-xs underline self-center ml-auto"
               style={{ color: ADMIN.muted }}>Apri preview ↗</a>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}


/**
 * Diagnostic + repair tool for the "video stays on 'Loading…'" bug.
 * Calls the admin `/probe` endpoint (checks if the MP4 moov atom is
 * before mdat) and exposes a one-click `/optimize` button that runs
 * ffmpeg +faststart on the asset already in Object Storage — no
 * re-upload required.
 */
function VideoFaststartTool({ lessonId, lang, faststartField, onChanged }) {
  const [probe, setProbe] = useState(null);
  const [busy, setBusy] = useState(null);

  const runProbe = async () => {
    setBusy("probe");
    try {
      const r = await apiGet(`/admin/vip/lessons/${lessonId}/video/${lang}/probe`);
      setProbe(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
    setBusy(null);
  };

  const runOptimize = async () => {
    if (!window.confirm(
      "Ottimizzare il video con ffmpeg +faststart? L'operazione può richiedere alcuni minuti per video lunghi."
    )) return;
    setBusy("opt");
    try {
      const r = await apiPost(`/admin/vip/lessons/${lessonId}/video/${lang}/optimize`);
      toast.success(`Video ottimizzato (${r.size_mb} MB)`);
      setProbe(null);
      await onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "ffmpeg non disponibile sul server");
    }
    setBusy(null);
  };

  return (
    <div className="ml-auto self-center flex flex-col items-end gap-1">
      <div className="flex gap-1.5">
        <Btn variant="soft" icon={faststartField === false ? AlertCircle : CheckCircle2}
             size="sm" disabled={busy !== null}
             onClick={runProbe}
             testId={`admin-video-${lang}-probe-btn`}>
          {busy === "probe" ? "…" : "Diagnostica"}
        </Btn>
        {(probe && probe.faststart_ok === false) || faststartField === false ? (
          <Btn variant="primary" icon={Wand2} size="sm"
               disabled={busy !== null}
               onClick={runOptimize}
               testId={`admin-video-${lang}-optimize-btn`}>
            {busy === "opt" ? "Ottimizzo…" : "Riparare"}
          </Btn>
        ) : null}
      </div>
      {probe ? (
        <p className="text-[10px] text-right max-w-[260px]"
           style={{ color: probe.faststart_ok ? "#107F52" : "#9E2A48" }}
           data-testid={`admin-video-${lang}-probe-result`}>
          {probe.faststart_ok ? "✓ Faststart OK" : "⚠️ moov al final — i browser caricano l'intero file prima di riprodurre"}
        </p>
      ) : null}
    </div>
  );
}


export function VideoPanel({ lessonId, lesson, onRefresh }) {
  // Phase 6.1 safety net: warn the admin when the server CANNOT auto-
  // optimize MP4 uploads (no ffmpeg on $PATH). Without this they'd only
  // discover the problem after a student reports an infinite spinner.
  const [ffmpegAvailable, setFfmpegAvailable] = useState(true);
  useEffect(() => {
    apiGet("/admin/system/faststart-status")
      .then((r) => setFfmpegAvailable(r?.ffmpeg_available !== false))
      .catch(() => setFfmpegAvailable(true)); // fail-open: don't block uploads
  }, []);
  return (
    <Section title="Video della lezione · Video de la clase" action={
      <span className="text-xs flex items-center gap-1" style={{ color: ADMIN.muted }}>
        <VideoIcon className="h-3.5 w-3.5" /> Bilingue · IT/ES indipendenti
      </span>
    }>
      {!ffmpegAvailable && (
        <div className="rounded-lg border px-3 py-2.5 text-xs mb-3 flex items-start gap-2"
             style={{
               background: "linear-gradient(135deg,#FFF7E6 0%,#FFEED1 100%)",
               borderColor: "#B45309", color: "#7A4F00",
             }}
             data-testid="admin-video-ffmpeg-warning">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <div>
            <strong>ffmpeg non disponibile sul server.</strong>{" "}
            I nuovi upload <strong>non verranno auto-ottimizzati</strong> e potrebbero
            mostrare un caricamento infinito agli studenti. Soluzione:
            esporta il video con HandBrake (preset <em>Fast 1080p30</em> + casella
            <strong> Web Optimized</strong> ✓), oppure usa{" "}
            <code className="text-[10px] bg-white/60 px-1 py-0.5 rounded">
              ffmpeg -i video.mp4 -c copy -movflags +faststart video_web.mp4
            </code>
            {" "}prima di caricare.
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <VideoLangPanel lang="it" lessonId={lessonId} video={lesson.video_it} onRefresh={onRefresh} />
        <VideoLangPanel lang="es" lessonId={lessonId} video={lesson.video_es} onRefresh={onRefresh} />
      </div>
    </Section>
  );
}


function Field({ label, value, onChange, multiline = false, rows = 3, small = false, testId }) {
  return (
    <label className="block">
      {label ? (
        <span className="block text-[11px] uppercase tracking-[0.16em] mb-1"
              style={{ color: ADMIN.muted }}>{label}</span>
      ) : null}
      {multiline ? (
        <textarea
          data-testid={testId}
          rows={rows}
          value={value} onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-lg border px-3 py-2 text-sm font-mono leading-relaxed"
          style={{ borderColor: ADMIN.border, color: ADMIN.text, background: "white" }}
        />
      ) : (
        <input
          data-testid={testId}
          value={value} onChange={(e) => onChange(e.target.value)}
          className={`w-full rounded-lg border px-3 ${small ? "py-1.5 text-xs" : "py-2 text-sm"}`}
          style={{ borderColor: ADMIN.border, color: ADMIN.text, background: "white" }}
        />
      )}
    </label>
  );
}

// ============== Generate (LLM + TTS) ==============
export function VIPAdminGenerate() {
  const [modules, setModules] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [moduleId, setModuleId] = useState("");
  const [chapterN, setChapterN] = useState("");
  const [sourceTitle, setSourceTitle] = useState("");
  const [sourceText, setSourceText] = useState("");
  const [synthAudio, setSynthAudio] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const [newModuleSlug, setNewModuleSlug] = useState("");
  const [newModuleIt, setNewModuleIt] = useState("");
  const [newModuleEs, setNewModuleEs] = useState("");

  useEffect(() => {
    apiGet("/admin/vip/modules").then(setModules);
    apiGet("/admin/vip/pdf/chapters").then((d) => setChapters(d.chapters || []));
  }, []);

  const createModule = async () => {
    if (!newModuleSlug || !newModuleIt || !newModuleEs) {
      toast.error("Slug + titoli IT/ES obbligatori"); return;
    }
    try {
      await apiPost("/admin/vip/modules", {
        slug: newModuleSlug, title_it: newModuleIt, title_es: newModuleEs,
        order: modules.length + 1,
      });
      toast.success("Modulo creato");
      setNewModuleSlug(""); setNewModuleIt(""); setNewModuleEs("");
      const fresh = await apiGet("/admin/vip/modules"); setModules(fresh);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const generate = async () => {
    if (!moduleId) return toast.error("Seleziona un modulo");
    if (!chapterN && (!sourceTitle || !sourceText)) {
      return toast.error("Seleziona un capitolo PDF OPPURE inserisci testo manuale");
    }
    setBusy(true); setResult(null);
    try {
      const body = {
        module_id: moduleId,
        synth_audio: synthAudio,
        ...(chapterN
          ? { chapter_n: parseInt(chapterN, 10) }
          : { source_title: sourceTitle, source_text: sourceText }),
      };
      const r = await apiPost("/admin/vip/lessons/generate", body);
      toast.success("Lezione generata!");
      setResult(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore generazione");
    }
    setBusy(false);
  };

  return (
    <div className="space-y-6" data-testid="admin-generate">
      <Section title="Crea nuovo modulo">
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Slug (es. modulo-2-pediatria)" value={newModuleSlug}
                 onChange={setNewModuleSlug} testId="new-module-slug" />
          <Field label="Titolo IT" value={newModuleIt} onChange={setNewModuleIt}
                 testId="new-module-it" />
          <Field label="Título ES" value={newModuleEs} onChange={setNewModuleEs}
                 testId="new-module-es" />
        </div>
        <div className="mt-3">
          <Btn icon={Plus} onClick={createModule} testId="new-module-create-btn">
            Crea modulo
          </Btn>
        </div>
      </Section>

      <Section title="Genera lezione (LLM + TTS)">
        <div className="space-y-4">
          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.16em] mb-1"
                  style={{ color: ADMIN.muted }}>Modulo destinazione</span>
            <select value={moduleId} onChange={(e) => setModuleId(e.target.value)}
                    data-testid="gen-module-select"
                    className="w-full rounded-lg border px-3 py-2 text-sm"
                    style={{ borderColor: ADMIN.border, color: ADMIN.text, background: "white" }}>
              <option value="">— Seleziona —</option>
              {modules.map((m) => (
                <option key={m.module_id} value={m.module_id}>
                  {m.title_it} ({m.lesson_count || 0} lezioni)
                </option>
              ))}
            </select>
          </label>

          <div className="rounded-lg border p-3" style={{ borderColor: ADMIN.border, background: ADMIN.primarySoft }}>
            <p className="text-xs uppercase tracking-wider mb-2" style={{ color: ADMIN.primary }}>
              Sorgente · Opzione A: Capitolo del PDF
            </p>
            <select value={chapterN}
                    data-testid="gen-chapter-select"
                    onChange={(e) => { setChapterN(e.target.value); setSourceText(""); setSourceTitle(""); }}
                    className="w-full rounded-lg border px-3 py-2 text-sm"
                    style={{ borderColor: ADMIN.border, color: ADMIN.text, background: "white" }}>
              <option value="">— Nessun capitolo selezionato —</option>
              {chapters.map((c) => (
                <option key={c.n} value={c.n}>
                  {c.n} · {c.title} (pg {c.start_page}-{c.end_page}, {c.char_count.toLocaleString()} chars)
                </option>
              ))}
            </select>
          </div>

          <div className="rounded-lg border p-3" style={{ borderColor: ADMIN.border }}>
            <p className="text-xs uppercase tracking-wider mb-2" style={{ color: ADMIN.muted }}>
              Sorgente · Opzione B: Testo manuale
            </p>
            <Field label="Titolo sorgente" value={sourceTitle} onChange={(v) => { setSourceTitle(v); setChapterN(""); }} />
            <div className="mt-2">
              <Field label="Testo (italiano, max 8000 chars)" value={sourceText}
                     onChange={(v) => { setSourceText(v); setChapterN(""); }}
                     multiline rows={8} />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm" style={{ color: ADMIN.text }}>
            <input type="checkbox" data-testid="gen-synth-audio"
                   checked={synthAudio} onChange={(e) => setSynthAudio(e.target.checked)} />
            Genera audio IT + ES (TTS) dopo
          </label>

          <Btn icon={Wand2} variant="primary" disabled={busy} onClick={generate}
               testId="gen-submit">
            {busy ? "Generazione in corso (può richiedere 1-3 min)…" : "Genera lezione"}
          </Btn>

          {result ? (
            <div className="rounded-lg border p-4" style={{ borderColor: ADMIN.border, background: "#F0FDF4" }}
                 data-testid="gen-result">
              <p className="font-medium" style={{ color: ADMIN.success }}>
                ✓ Lezione creata: {result.title_it}
              </p>
              <p className="text-sm mt-1" style={{ color: ADMIN.text }}>
                {result.title_es} · {result.questions_count} domande · {result.slides_count} slide ·
                Audio: <strong>{result.audio_status}</strong>
              </p>
              <Link to={`/admin/vip/lessons/${result.lesson_id}`}
                    className="text-sm mt-2 inline-block" style={{ color: ADMIN.primary }}>
                Modifica → 
              </Link>
            </div>
          ) : null}
        </div>
      </Section>
    </div>
  );
}

// ============== Cost dashboard ==============
export function VIPAdminCosts() {
  const [data, setData] = useState(null);
  useEffect(() => {
    apiGet("/admin/vip/costs").then(setData)
      .catch((e) => toast.error(e?.response?.data?.detail || "Errore"));
  }, []);
  if (!data) return <Loading />;
  return (
    <div className="space-y-5" data-testid="admin-costs">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Lezioni" value={data.lesson_count} />
        <Stat label="Caratteri TTS" value={data.total_chars_tts.toLocaleString()} sub="totale" />
        <Stat label="LLM stimato" value={fmtUSD(data.total_llm_cost_estimate)}
              sub={data.model_llm} color={ADMIN.primary} />
        <Stat label="TTS reale" value={fmtUSD(data.total_tts_cost)}
              sub={data.model_tts} color={ADMIN.success} />
      </div>
      <Section title={`Totale stimato: ${fmtUSD(data.grand_total_estimate)}`}>
        <div className="-mx-5 overflow-x-auto">
          <table className="w-full text-sm">
            <thead style={{ background: ADMIN.primarySoft, color: ADMIN.muted }}>
              <tr className="text-left">
                <th className="px-5 py-2 text-[11px] uppercase tracking-wider">Lezione</th>
                <th className="px-3 py-2 text-[11px] uppercase tracking-wider">Audio</th>
                <th className="px-3 py-2 text-[11px] uppercase tracking-wider text-right">Char TTS</th>
                <th className="px-3 py-2 text-[11px] uppercase tracking-wider text-right">LLM</th>
                <th className="px-3 py-2 text-[11px] uppercase tracking-wider text-right">TTS</th>
                <th className="px-5 py-2 text-[11px] uppercase tracking-wider text-right">Totale</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.lesson_id} className="border-t" style={{ borderColor: ADMIN.border }}>
                  <td className="px-5 py-2.5" style={{ color: ADMIN.text }}>{r.title_it}</td>
                  <td className="px-3 py-2.5">{r.audio_status === "ready"
                    ? <Badge kind="success">Ready</Badge>
                    : <Badge kind="muted">{r.audio_status || "—"}</Badge>}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums" style={{ color: ADMIN.muted }}>
                    {r.body_chars.toLocaleString()}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums" style={{ color: ADMIN.muted }}>
                    {fmtUSD(r.llm_cost_estimate)}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums" style={{ color: ADMIN.muted }}>
                    {fmtUSD(r.tts_cost)}
                  </td>
                  <td className="px-5 py-2.5 text-right tabular-nums font-medium"
                      style={{ color: ADMIN.text }}>
                    {fmtUSD(r.total_cost)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs mt-3" style={{ color: ADMIN.muted }}>
          Stime basate su prezzi pubblici OpenAI · LLM input ~2000 tok / output ~3500 tok per lezione (euristica).
        </p>
      </Section>
    </div>
  );
}

// ============== Users dashboard ==============
export function VIPAdminUsers() {
  const [progress, setProgress] = useState(null);
  const [academy, setAcademy] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = () => {
    apiGet("/admin/vip/users/progress").then(setProgress)
      .catch((e) => toast.error(e?.response?.data?.detail || "Errore progresso"));
    apiGet("/admin/vip/users/academy").then(setAcademy)
      .catch((e) => toast.error(e?.response?.data?.detail || "Errore academy"));
  };
  useEffect(reload, []);

  const triggerReminders = async () => {
    setBusy(true);
    try {
      const r = await apiPost("/admin/cron/run-academy-reminders", {});
      const total = Object.values(r || {}).reduce((s, v) => s + (v || 0), 0);
      toast.success(`Promemoria inviati: ${total} (30d:${r["30d"] || 0} · 7d:${r["7d"] || 0} · 1d:${r["1d"] || 0} · scaduti:${r.expired || 0})`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore cron");
    }
    setBusy(false);
  };

  if (!progress || !academy) return <Loading />;

  const summary = academy.users.reduce(
    (acc, u) => {
      acc[u.tier] = (acc[u.tier] || 0) + 1;
      return acc;
    },
    { active: 0, lifetime: 0, expired: 0, none: 0 },
  );

  return (
    <div className="space-y-6" data-testid="vip-admin-users">
      <Section
        title={`Academia · stato commerciale (${academy.count})`}
        action={
          <Btn icon={RefreshCw} onClick={triggerReminders} disabled={busy}
               testId="btn-trigger-academy-reminders" size="sm" variant="soft">
            {busy ? "Invio…" : "Triggera promemoria"}
          </Btn>
        }>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5" data-testid="academy-tier-counts">
          <StatPill label="Attivi" value={summary.active} tone="success" />
          <StatPill label="Lifetime" value={summary.lifetime} tone="primary" />
          <StatPill label="Scaduti" value={summary.expired} tone="danger" />
          <StatPill label="Tot" value={academy.count} tone="muted" />
        </div>
        {academy.users.length === 0 ? (
          <p className="text-sm py-6 text-center" style={{ color: ADMIN.muted }}>
            Nessun utente Academia.
          </p>
        ) : (
          <div className="-mx-5 overflow-x-auto">
            <table className="w-full text-sm" data-testid="academy-tracking-table">
              <thead style={{ background: ADMIN.primarySoft, color: ADMIN.muted }}>
                <tr className="text-left">
                  <th className="px-5 py-2 text-[11px] uppercase tracking-wider">Utente</th>
                  <th className="px-3 py-2 text-[11px] uppercase tracking-wider">Tier</th>
                  <th className="px-3 py-2 text-[11px] uppercase tracking-wider">Scadenza</th>
                  <th className="px-3 py-2 text-[11px] uppercase tracking-wider text-right">Giorni</th>
                  <th className="px-3 py-2 text-[11px] uppercase tracking-wider text-center">Promemoria 30d</th>
                  <th className="px-3 py-2 text-[11px] uppercase tracking-wider text-center">7d</th>
                  <th className="px-3 py-2 text-[11px] uppercase tracking-wider text-center">1d</th>
                  <th className="px-3 py-2 text-[11px] uppercase tracking-wider text-center">Scaduto</th>
                  <th className="px-5 py-2 text-[11px] uppercase tracking-wider text-right">Rinnovi</th>
                </tr>
              </thead>
              <tbody>
                {academy.users.map((u) => (
                  <tr key={u.user_id} data-testid={`academy-row-${u.user_id}`}
                      className="border-t" style={{ borderColor: ADMIN.border }}>
                    <td className="px-5 py-2.5">
                      <p style={{ color: ADMIN.text }}>{u.name || "—"}</p>
                      <p className="text-xs" style={{ color: ADMIN.muted }}>{u.email}</p>
                    </td>
                    <td className="px-3 py-2.5">
                      <TierBadge tier={u.tier} />
                    </td>
                    <td className="px-3 py-2.5 text-xs" style={{ color: ADMIN.muted }}>
                      {u.academy_expires_at
                        ? new Date(u.academy_expires_at).toLocaleDateString("it-IT")
                        : (u.tier === "lifetime" ? "—" : "—")}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-xs"
                        style={{
                          color: u.days_left === null ? ADMIN.muted
                            : u.days_left < 0 ? ADMIN.danger
                            : u.days_left <= 7 ? ADMIN.warn
                            : ADMIN.text,
                        }}>
                      {u.days_left === null ? "—" : u.days_left}
                    </td>
                    <ReminderCell sentAt={u.reminder_30d_sent_at} />
                    <ReminderCell sentAt={u.reminder_7d_sent_at} />
                    <ReminderCell sentAt={u.reminder_1d_sent_at} />
                    <ReminderCell sentAt={u.reminder_expired_sent_at} danger />
                    <td className="px-5 py-2.5 text-right tabular-nums" style={{ color: ADMIN.text }}>
                      {u.academy_renewals_count || 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title={`Progresso lezioni · utenti VIP (${progress.count})`}>
        {progress.users.length === 0 ? (
          <p className="text-sm py-8 text-center" style={{ color: ADMIN.muted }}>
            Nessun utente VIP attivo.
          </p>
        ) : (
          <div className="-mx-5 overflow-x-auto">
            <table className="w-full text-sm">
              <thead style={{ background: ADMIN.primarySoft, color: ADMIN.muted }}>
                <tr className="text-left">
                  <th className="px-5 py-2 text-[11px] uppercase tracking-wider">Utente</th>
                  <th className="px-3 py-2 text-[11px] uppercase tracking-wider">Lingua</th>
                  <th className="px-3 py-2 text-[11px] uppercase tracking-wider">Acquistato</th>
                  <th className="px-3 py-2 text-[11px] uppercase tracking-wider text-right">Iniziate</th>
                  <th className="px-5 py-2 text-[11px] uppercase tracking-wider text-right">Completate</th>
                </tr>
              </thead>
              <tbody>
                {progress.users.map((u) => (
                  <tr key={u.user_id} className="border-t" style={{ borderColor: ADMIN.border }}>
                    <td className="px-5 py-2.5">
                      <p style={{ color: ADMIN.text }}>{u.name || "—"}</p>
                      <p className="text-xs" style={{ color: ADMIN.muted }}>{u.email}</p>
                    </td>
                    <td className="px-3 py-2.5">
                      <Badge kind="muted">{u.preferred_language || "it"}</Badge>
                    </td>
                    <td className="px-3 py-2.5 text-xs" style={{ color: ADMIN.muted }}>
                      {u.vip_purchased_at ? new Date(u.vip_purchased_at).toLocaleDateString("it-IT") : "—"}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums" style={{ color: ADMIN.muted }}>
                      {u.lessons_started}
                    </td>
                    <td className="px-5 py-2.5 text-right tabular-nums font-medium"
                        style={{ color: ADMIN.text }}>
                      {u.lessons_completed}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}

function StatPill({ label, value, tone = "muted" }) {
  const toneMap = {
    success: { bg: "#E6F4EC", color: ADMIN.success },
    primary: { bg: ADMIN.primarySoft, color: ADMIN.primary },
    danger: { bg: "#FCE7EB", color: ADMIN.danger },
    muted: { bg: "#F1EEF8", color: ADMIN.muted },
  }[tone];
  return (
    <div className="rounded-xl px-4 py-3" style={{ background: toneMap.bg }}
         data-testid={`stat-pill-${label.toLowerCase()}`}>
      <p className="text-[10px] uppercase tracking-widest font-medium" style={{ color: toneMap.color, opacity: 0.8 }}>
        {label}
      </p>
      <p className="text-2xl font-serif tabular-nums" style={{ color: toneMap.color }}>{value}</p>
    </div>
  );
}

function TierBadge({ tier }) {
  const map = {
    active: { kind: "success", label: "Attivo" },
    lifetime: { kind: "default", label: "Lifetime" },
    expired: { kind: "danger", label: "Scaduto" },
    none: { kind: "muted", label: "—" },
  };
  const m = map[tier] || map.none;
  return <Badge kind={m.kind}>{m.label}</Badge>;
}

function ReminderCell({ sentAt, danger = false }) {
  if (!sentAt) {
    return (
      <td className="px-3 py-2.5 text-center text-xs" style={{ color: ADMIN.muted }}>
        —
      </td>
    );
  }
  let label = "—";
  try {
    label = new Date(sentAt).toLocaleDateString("it-IT", {
      day: "2-digit", month: "2-digit",
    });
  } catch { /* noop */ }
  return (
    <td className="px-3 py-2.5 text-center">
      <span className="inline-flex items-center gap-1 text-xs"
            style={{ color: danger ? ADMIN.danger : ADMIN.success }}>
        <CheckCircle2 className="h-3 w-3" />
        {label}
      </span>
    </td>
  );
}


// ============== Migration: export ZIP / import ZIP ==============
export function VIPAdminMigrate() {
  const [busy, setBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [confirmImport, setConfirmImport] = useState(null);

  const handleExport = async () => {
    setBusy(true);
    try {
      // Use raw fetch to access streaming Blob — apiGet decodes JSON.
      const tok = localStorage.getItem("mediquiz_session") || "";
      const r = await fetch(`${API}/admin/vip/export`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vip-content-${new Date().toISOString().slice(0,10)}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export scaricato");
    } catch (e) { toast.error("Errore export: " + e.message); }
    setBusy(false);
  };

  const handleFilePick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.endsWith(".zip")) { toast.error("Serve un file .zip"); return; }
    setConfirmImport(f);
    e.target.value = ""; // allow same file twice
  };

  const handleImport = async () => {
    if (!confirmImport) return;
    setBusy(true);
    try {
      const tok = localStorage.getItem("mediquiz_session") || "";
      const buf = await confirmImport.arrayBuffer();
      const r = await fetch(`${API}/admin/vip/import`, {
        method: "POST",
        headers: { Authorization: `Bearer ${tok}`, "Content-Type": "application/zip" },
        body: buf,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || `HTTP ${r.status}`);
      setImportResult(data);
      toast.success(`Import OK · ${data.summary.lessons.upserted} lezioni`);
      setConfirmImport(null);
    } catch (e) { toast.error("Errore import: " + e.message); }
    setBusy(false);
  };

  return (
    <div className="space-y-6" data-testid="admin-migrate">
      <Section title="Esporta contenuto">
        <p className="text-sm mb-4 leading-relaxed" style={{ color: ADMIN.muted }}>
          Scarica un file ZIP con tutti i moduli, le lezioni (corpo IT/ES, slide, domande)
          e gli audio MP3. Stesso formato per import in produzione: i <code className="px-1 rounded"
          style={{ background: ADMIN.primarySoft, fontSize: "12px" }}>lesson_id</code> e
          <code className="px-1 rounded" style={{ background: ADMIN.primarySoft, fontSize: "12px" }}> module_id</code>
          {" "}vengono preservati per non rompere i progressi degli utenti.
        </p>
        <Btn icon={Download} onClick={handleExport} disabled={busy}
             testId="admin-migrate-export">
          {busy ? "Esportazione…" : "Esporta tutto come ZIP"}
        </Btn>
      </Section>

      <Section title="Importa contenuto">
        <p className="text-sm mb-4 leading-relaxed" style={{ color: ADMIN.muted }}>
          Carica un ZIP esportato da un altro ambiente (es. preview → produzione). I moduli
          e le lezioni esistenti con lo stesso ID verranno <strong>aggiornati</strong>; i nuovi
          verranno creati. Niente viene eliminato.
        </p>
        <input type="file" accept=".zip" onChange={handleFilePick}
               data-testid="admin-migrate-file"
               className="block w-full text-sm rounded-lg border px-3 py-2"
               style={{ borderColor: ADMIN.border, color: ADMIN.text, background: "white" }} />
        {importResult ? (
          <div className="mt-4 rounded-lg border p-4" style={{ borderColor: ADMIN.border, background: "#F0FDF4" }}
               data-testid="admin-migrate-result">
            <p className="font-medium" style={{ color: ADMIN.success }}>✓ Import completato</p>
            <pre className="text-xs mt-2 font-mono" style={{ color: ADMIN.text }}>
              {JSON.stringify(importResult.summary, null, 2)}
            </pre>
          </div>
        ) : null}
      </Section>

      {confirmImport ? (
        <div data-testid="admin-import-modal"
             className="fixed inset-0 z-50 grid place-items-center p-4"
             style={{ background: "rgba(20,15,40,0.55)" }}
             onClick={(e) => e.target === e.currentTarget && setConfirmImport(null)}>
          <div className="rounded-2xl bg-white shadow-2xl max-w-md w-full p-6 border"
               style={{ borderColor: ADMIN.border }}>
            <div className="flex items-start gap-3 mb-4">
              <div className="shrink-0 h-10 w-10 rounded-full grid place-items-center"
                   style={{ background: "#FEF3C7" }}>
                <AlertTriangle className="h-5 w-5" style={{ color: ADMIN.warn }} />
              </div>
              <div>
                <h3 className="font-serif text-xl" style={{ color: ADMIN.text }}>
                  Confermi l'import?
                </h3>
                <p className="text-sm mt-1" style={{ color: ADMIN.muted }}>
                  File: <strong>{confirmImport.name}</strong> ({Math.round(confirmImport.size / 1024)} KB)
                  <br />
                  Moduli e lezioni con lo stesso ID verranno sovrascritti.
                </p>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Btn variant="ghost" onClick={() => setConfirmImport(null)} disabled={busy}>
                Annulla
              </Btn>
              <Btn variant="primary" icon={Upload} onClick={handleImport} disabled={busy}
                   testId="admin-import-confirm">
                {busy ? "Importazione…" : "Conferma import"}
              </Btn>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}


// ============== Funnel: Trial conversion analytics ==============
//
// Stats panel for the FREE → Academy purchase funnel. Pulls from
// /api/admin/vip/trial-offer/stats?days=N which aggregates events from
// the `vip_funnel_events` collection. Six-stage funnel:
//   l1_viewed → l1_completed → modal_shown → trial_started → cta_clicked → converted
// Distinct-user counts at each stage avoid skew from heavy users.

const FUNNEL_STAGES = [
  { key: "vip_l1_viewed",     label: "L1 vista",         color: "#A78BFA" },
  { key: "vip_l1_completed",  label: "L1 completata",    color: "#8B5CF6" },
  { key: "trial_modal_shown", label: "Modal mostrato",   color: "#7C3AED" },
  { key: "trial_started",     label: "Trial iniziato",   color: "#6D28D9" },
  { key: "trial_cta_clicked", label: "CTA cliccato",     color: "#5B21B6" },
  { key: "trial_converted",   label: "Convertito",       color: "#1A8A53" },
];

const FUNNEL_LABEL = Object.fromEntries(FUNNEL_STAGES.map((s) => [s.key, s.label]));

function ConversionRow({ stage, idx, rates }) {
  const r = rates?.[stage.key];
  if (!r) return null;
  const fromPrev = r.rate_from_prev_pct;
  const fromTop = r.rate_from_top_pct;
  return (
    <tr className="border-b" style={{ borderColor: ADMIN.border }} data-testid={`funnel-row-${stage.key}`}>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: stage.color }} />
          <span className="text-sm" style={{ color: ADMIN.text }}>
            {idx + 1}. {stage.label}
          </span>
        </div>
      </td>
      <td className="px-4 py-3 text-right tabular-nums font-medium" style={{ color: ADMIN.text }}>
        {r.users}
      </td>
      <td className="px-4 py-3 text-right tabular-nums" style={{ color: ADMIN.muted }}>
        {fromPrev === null ? "—" : `${fromPrev}%`}
      </td>
      <td className="px-4 py-3 text-right tabular-nums" style={{ color: ADMIN.muted }}>
        {fromTop}%
      </td>
    </tr>
  );
}

function RecentEventsTable({ events }) {
  if (!events?.length) {
    return (
      <p className="text-sm text-center py-6" style={{ color: ADMIN.muted }} data-testid="funnel-recent-empty">
        Nessun evento ancora registrato
      </p>
    );
  }
  return (
    <table className="w-full text-sm" data-testid="funnel-recent-table">
      <thead>
        <tr className="border-b" style={{ borderColor: ADMIN.border }}>
          <th className="px-3 py-2 text-left text-[11px] uppercase tracking-[0.16em] font-medium" style={{ color: ADMIN.muted }}>Quando</th>
          <th className="px-3 py-2 text-left text-[11px] uppercase tracking-[0.16em] font-medium" style={{ color: ADMIN.muted }}>Utente</th>
          <th className="px-3 py-2 text-left text-[11px] uppercase tracking-[0.16em] font-medium" style={{ color: ADMIN.muted }}>Evento</th>
        </tr>
      </thead>
      <tbody>
        {events.map((e, i) => {
          let when = "—";
          try { when = new Date(e.ts).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { /* noop */ }
          return (
            <tr key={`${e.user_id}-${e.ts}-${i}`} className="border-b" style={{ borderColor: ADMIN.border }}>
              <td className="px-3 py-2 tabular-nums" style={{ color: ADMIN.muted }}>{when}</td>
              <td className="px-3 py-2 truncate max-w-[220px]" style={{ color: ADMIN.text }} title={e.email}>
                {e.email}
              </td>
              <td className="px-3 py-2">
                <Badge>{FUNNEL_LABEL[e.event_type] || e.event_type}</Badge>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export function VIPAdminFunnel() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = React.useCallback(async (n) => {
    setLoading(true);
    try {
      const r = await apiGet(`/admin/vip/trial-offer/stats?days=${n}`);
      setData(r);
    } catch (e) {
      toast.error("Errore caricamento metriche: " + (e.response?.data?.detail || e.message));
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(days); }, [days, load]);

  const totals = data?.totals || {};
  const distinct = data?.distinct_users || {};
  const rates = data?.rates || {};
  const daily = data?.daily || [];

  // Headline conversion: % of L1 viewers that ended up purchasing academy
  const overallRate = rates?.trial_converted?.rate_from_top_pct ?? 0;
  // Time spent at each step — useful when the volume column makes the small
  // Convert number visually invisible.

  return (
    <div className="space-y-6" data-testid="admin-funnel">
      {/* Header + window picker */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] mb-1" style={{ color: ADMIN.muted }}>
            Funnel di conversione
          </p>
          <h2 className="font-serif text-2xl" style={{ color: ADMIN.text }}>
            Trial 24h · ultimi {days} giorni
          </h2>
        </div>
        <div className="flex items-center gap-1 rounded-lg border p-1" style={{ borderColor: ADMIN.border, background: "white" }}>
          {[7, 30, 90].map((n) => (
            <button key={n}
                    data-testid={`funnel-window-${n}d`}
                    onClick={() => setDays(n)}
                    className="px-3 py-1.5 rounded-md text-xs font-medium transition-all"
                    style={{
                      background: days === n ? ADMIN.primary : "transparent",
                      color: days === n ? "white" : ADMIN.muted,
                    }}>
              {n}g
            </button>
          ))}
          <button onClick={() => load(days)} disabled={loading}
                  className="px-2 py-1.5 rounded-md transition-all hover:opacity-80 disabled:opacity-40"
                  style={{ color: ADMIN.muted }}
                  data-testid="funnel-refresh"
                  aria-label="Aggiorna">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Headline KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="funnel-kpis">
        {FUNNEL_STAGES.map((s) => (
          <Stat key={s.key}
                label={s.label}
                value={fmt(distinct[s.key] ?? 0)}
                sub={`${totals[s.key] ?? 0} eventi`}
                color={s.color} />
        ))}
      </div>

      {/* Headline conversion rate */}
      <div className="rounded-2xl border p-5"
           style={{ borderColor: ADMIN.border, background: `linear-gradient(135deg, ${ADMIN.primarySoft} 0%, white 60%)` }}
           data-testid="funnel-overall-rate">
        <p className="text-[11px] uppercase tracking-[0.18em]" style={{ color: ADMIN.primary }}>
          Tasso di conversione globale
        </p>
        <p className="font-serif text-4xl mt-1 tabular-nums" style={{ color: ADMIN.text }}>
          {overallRate}%
        </p>
        <p className="text-xs mt-1" style={{ color: ADMIN.muted }}>
          Utenti distinti che vedono L1 → comprano l'Accademia
        </p>
      </div>

      {/* Daily chart */}
      <Section title="Eventi giornalieri">
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin" style={{ color: ADMIN.muted }} />
          </div>
        ) : daily.length === 0 ? (
          <p className="text-sm text-center py-6" style={{ color: ADMIN.muted }}>
            Nessun dato per il periodo selezionato
          </p>
        ) : (
          <div className="h-64" data-testid="funnel-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={daily} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={ADMIN.border} vertical={false} />
                <XAxis dataKey="day" stroke={ADMIN.muted}
                       tick={{ fontSize: 10 }}
                       tickFormatter={(d) => d.slice(5)} />
                <YAxis stroke={ADMIN.muted} tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "white", border: `1px solid ${ADMIN.border}`, borderRadius: 8, fontSize: 12 }}
                         formatter={(v, name) => [v, FUNNEL_LABEL[name] || name]} />
                <Legend wrapperStyle={{ fontSize: 11 }}
                        formatter={(name) => FUNNEL_LABEL[name] || name} />
                {FUNNEL_STAGES.map((s) => (
                  <Area key={s.key}
                        type="monotone"
                        dataKey={s.key}
                        stackId="1"
                        stroke={s.color}
                        fill={s.color}
                        fillOpacity={0.35} />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </Section>

      {/* Funnel breakdown table */}
      <Section title="Tasso di conversione per stage">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="funnel-rates-table">
            <thead>
              <tr className="border-b" style={{ borderColor: ADMIN.border, background: ADMIN.primarySoft }}>
                <th className="px-4 py-2 text-left text-[11px] uppercase tracking-[0.16em] font-medium" style={{ color: ADMIN.muted }}>Stage</th>
                <th className="px-4 py-2 text-right text-[11px] uppercase tracking-[0.16em] font-medium" style={{ color: ADMIN.muted }}>Utenti</th>
                <th className="px-4 py-2 text-right text-[11px] uppercase tracking-[0.16em] font-medium" style={{ color: ADMIN.muted }}>vs precedente</th>
                <th className="px-4 py-2 text-right text-[11px] uppercase tracking-[0.16em] font-medium" style={{ color: ADMIN.muted }}>vs L1 vista</th>
              </tr>
            </thead>
            <tbody>
              {FUNNEL_STAGES.map((s, i) => (
                <ConversionRow key={s.key} stage={s} idx={i} rates={rates} />
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Recent events */}
      <Section title="Eventi recenti">
        <RecentEventsTable events={data?.recent} />
      </Section>
    </div>
  );
}


// ====================================================================
// VIP ADMIN · VIDEOS · aggregate per-lesson view (IT/ES status + toggles)
// ====================================================================

function VideoStatusBadge({ summary }) {
  // summary: { kind: "upload"|"url"|null, published: bool, external_url? }
  if (!summary || !summary.kind) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider"
            style={{ background: "#F3F1F8", color: ADMIN.muted }}>
        <span className="h-1.5 w-1.5 rounded-full bg-gray-400" /> vuoto
      </span>
    );
  }
  const isPublished = summary.published;
  const bg = isPublished ? "#DCFCE7" : "#FEF3C7";
  const fg = isPublished ? "#166534" : "#92400E";
  const dot = isPublished ? "#16A34A" : "#D97706";
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider"
          style={{ background: bg, color: fg }}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: dot }} />
      {isPublished ? "pubblicato" : "caricato"} · {summary.kind === "url" ? "link" : "mp4"}
    </span>
  );
}

export function VIPAdminVideos() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savingKey, setSavingKey] = useState(null);

  const refresh = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await apiGet("/admin/vip/videos-summary");
      setData(res);
    } catch (e) {
      setError(e?.response?.data?.detail || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const togglePublish = async (lesson, lang) => {
    const summary = lesson[`video_${lang}`];
    if (!summary || !summary.kind) {
      toast.error(`Nessun video ${lang.toUpperCase()} da pubblicare per questa lezione`);
      return;
    }
    const key = `${lesson.lesson_id}:${lang}`;
    setSavingKey(key);
    try {
      await apiPatch(`/admin/vip/lessons/${lesson.lesson_id}/video`, {
        lang, published: !summary.published,
      });
      toast.success(`Video ${lang.toUpperCase()} ${!summary.published ? "pubblicato" : "nascosto"}`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setSavingKey(null);
    }
  };

  if (loading) {
    return <div className="max-w-6xl mx-auto p-6 flex items-center gap-2 text-sm" style={{ color: ADMIN.muted }}>
      <Loader2 className="h-4 w-4 animate-spin" /> Caricamento…
    </div>;
  }
  if (error) {
    return <div className="max-w-6xl mx-auto p-6 text-sm text-red-600">{error}</div>;
  }
  if (!data) return null;

  const moduleById = Object.fromEntries((data.modules || []).map(m => [m.module_id, m]));
  // Group lessons by module to render a clean per-module section.
  const grouped = {};
  for (const l of data.lessons) {
    (grouped[l.module_id] = grouped[l.module_id] || []).push(l);
  }
  const totalIt = data.lessons.filter(l => l.video_it.published).length;
  const totalEs = data.lessons.filter(l => l.video_es.published).length;
  const total = data.lessons.length;

  return (
    <div className="max-w-6xl mx-auto" data-testid="vip-admin-videos">
      <header className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em]" style={{ color: ADMIN.primary }}>
            Video · vista d'insieme
          </p>
          <h2 className="font-serif text-2xl" style={{ color: ADMIN.text }}>
            {total} lezioni · {totalIt} IT pubblicati · {totalEs} ES pubblicati
          </h2>
        </div>
        <button onClick={refresh} className="inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg"
                style={{ border: `1px solid ${ADMIN.border}`, background: "white", color: ADMIN.muted }}
                data-testid="videos-refresh">
          <RefreshCw className="h-3.5 w-3.5" /> Aggiorna
        </button>
      </header>

      {(data.modules || []).map((m) => {
        const lessons = grouped[m.module_id] || [];
        if (!lessons.length) return null;
        return (
          <section key={m.module_id} className="mb-8" data-testid={`videos-module-${m.module_id}`}>
            <h3 className="font-serif text-lg mb-3" style={{ color: ADMIN.text }}>{m.title_it}</h3>
            <div className="rounded-xl overflow-hidden" style={{ background: "white", border: `1px solid ${ADMIN.border}` }}>
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: ADMIN.primarySoft, color: ADMIN.primary }}>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold">Lezione</th>
                    <th className="px-3 py-2.5 text-xs font-semibold">Video IT</th>
                    <th className="px-3 py-2.5 text-xs font-semibold">Video ES</th>
                    <th className="px-3 py-2.5 text-xs font-semibold w-32">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {lessons.map((l) => (
                    <tr key={l.lesson_id} className="border-t" style={{ borderColor: ADMIN.border }}>
                      <td className="px-4 py-3">
                        <div className="text-[13px] font-medium" style={{ color: ADMIN.text }}>
                          L{l.order + 1} · {l.title_it}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <div className="flex flex-col items-center gap-1.5">
                          <VideoStatusBadge summary={l.video_it} />
                          {l.video_it.kind && (
                            <button
                              onClick={() => togglePublish(l, "it")}
                              disabled={savingKey === `${l.lesson_id}:it`}
                              className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded disabled:opacity-50"
                              style={{ background: l.video_it.published ? "#FEE2E2" : "#DCFCE7",
                                       color: l.video_it.published ? "#991B1B" : "#166534" }}
                              data-testid={`toggle-it-${l.lesson_id}`}>
                              {savingKey === `${l.lesson_id}:it` ? "…" :
                                l.video_it.published ? "Nascondi" : "Pubblica"}
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <div className="flex flex-col items-center gap-1.5">
                          <VideoStatusBadge summary={l.video_es} />
                          {l.video_es.kind && (
                            <button
                              onClick={() => togglePublish(l, "es")}
                              disabled={savingKey === `${l.lesson_id}:es`}
                              className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded disabled:opacity-50"
                              style={{ background: l.video_es.published ? "#FEE2E2" : "#DCFCE7",
                                       color: l.video_es.published ? "#991B1B" : "#166534" }}
                              data-testid={`toggle-es-${l.lesson_id}`}>
                              {savingKey === `${l.lesson_id}:es` ? "…" :
                                l.video_es.published ? "Nascondi" : "Pubblica"}
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <Link to={`/admin/vip/lessons/${l.lesson_id}`}
                              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded"
                              style={{ background: ADMIN.primary, color: "white" }}
                              data-testid={`videos-edit-${l.lesson_id}`}>
                          <Film className="h-3 w-3" /> Modifica
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}
    </div>
  );
}


// ====================================================================
// VIP ADMIN · PAGAMENTI · Stripe payment links verification
// ====================================================================

function StripeStatusBadge({ item }) {
  if (!item.configured) {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider"
                 style={{ background: "#FEF3C7", color: "#92400E" }}>
      <AlertTriangle className="h-3 w-3" /> non configurato
    </span>;
  }
  if (item.stripe_status === "ok" && item.stripe_active === true) {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider"
                 style={{ background: "#DCFCE7", color: "#166534" }}>
      <CheckCircle2 className="h-3 w-3" /> live
    </span>;
  }
  if (item.stripe_status === "ok" && item.stripe_active === false) {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider"
                 style={{ background: "#FEE2E2", color: "#991B1B" }}>
      <AlertCircle className="h-3 w-3" /> disattivato
    </span>;
  }
  if (item.stripe_status && item.stripe_status.startsWith("error")) {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider"
                 style={{ background: "#FEF3C7", color: "#92400E" }} title={item.stripe_status}>
      <AlertCircle className="h-3 w-3" /> errore stripe
    </span>;
  }
  // Configured but not verifiable (no stripe call ran)
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider"
               style={{ background: ADMIN.primarySoft, color: ADMIN.primary }}>
    <CheckCircle2 className="h-3 w-3" /> configurato
  </span>;
}

export function VIPAdminPayments() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    apiGet("/admin/vip/payments-config")
      .then(setData)
      .catch(e => setError(e?.response?.data?.detail || "Errore caricamento"))
      .finally(() => setLoading(false));
  }, []);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copiato`);
    } catch {
      toast.error("Impossibile copiare");
    }
  };

  if (loading) return <div className="max-w-6xl mx-auto p-6 flex items-center gap-2 text-sm" style={{ color: ADMIN.muted }}>
    <Loader2 className="h-4 w-4 animate-spin" /> Caricamento…
  </div>;
  if (error) return <div className="max-w-6xl mx-auto p-6 text-sm text-red-600">{error}</div>;
  if (!data) return null;

  return (
    <div className="max-w-6xl mx-auto" data-testid="vip-admin-payments">
      <header className="mb-6">
        <p className="text-[11px] uppercase tracking-[0.2em]" style={{ color: ADMIN.primary }}>
          Pagamenti · Stripe payment links
        </p>
        <h2 className="font-serif text-2xl" style={{ color: ADMIN.text }}>Verifica configurazione</h2>
        <p className="text-sm mt-1" style={{ color: ADMIN.muted }}>
          Modalità Stripe: <strong style={{ color: data.stripe_mode === "live" ? "#166534" : ADMIN.warn }}>
            {data.stripe_mode}
          </strong>
        </p>
      </header>

      <div className="space-y-3">
        {(data.plans || []).map((p) => (
          <div key={p.key} className="rounded-xl p-4" style={{ background: "white", border: `1px solid ${ADMIN.border}` }}
               data-testid={`payment-${p.key}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-medium" style={{ color: ADMIN.text }}>{p.label}</h3>
                  <StripeStatusBadge item={p} />
                </div>
                <p className="text-[11px] font-mono" style={{ color: ADMIN.muted }}>
                  ENV: {p.env_var}
                </p>
                {p.url && (
                  <p className="text-xs mt-1 truncate" style={{ color: ADMIN.muted }}>
                    {p.url}
                  </p>
                )}
                {p.stripe_amount_cents != null && p.expected_amount_cents != null && (
                  <p className="text-[11px] mt-1.5"
                     style={{ color: p.stripe_amount_cents === p.expected_amount_cents ? "#166534" : ADMIN.danger }}>
                    Importo Stripe: €{(p.stripe_amount_cents / 100).toFixed(2)}
                    {p.stripe_amount_cents !== p.expected_amount_cents
                      ? ` ⚠ atteso €${(p.expected_amount_cents / 100).toFixed(2)}` : " ✓"}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-2">
                {p.url ? (
                  <>
                    <button onClick={() => copy(p.url, p.label)}
                            className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded"
                            style={{ border: `1px solid ${ADMIN.border}`, color: ADMIN.muted, background: "white" }}
                            data-testid={`payment-copy-${p.key}`}>
                      <Copy className="h-3 w-3" /> Copia
                    </button>
                    <a href={p.url} target="_blank" rel="noopener noreferrer"
                       className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded text-white"
                       style={{ background: ADMIN.primary }}
                       data-testid={`payment-test-${p.key}`}>
                      <ExternalLink className="h-3 w-3" /> Prova
                    </a>
                  </>
                ) : (
                  <span className="text-xs italic" style={{ color: ADMIN.muted }}>
                    Aggiungi {p.env_var} in .env
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <p className="text-xs mt-6" style={{ color: ADMIN.muted }}>
        💡 Suggerimento: per i pacchetti del Simulatore Reale (esami €49.90 / €79.90),
        crea i Payment Links in Stripe Dashboard → Products, poi aggiungi le rispettive
        env vars al deploy. I link saranno usati automaticamente dal flusso pagamenti.
      </p>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// Security Reviews — anti-piracy strike appeal inbox for the admin.
// Lists pending appeals submitted by users via the SecurityWarningModal,
// each with the user's explanation + prior strike count. Admin can Approve
// (forgive the strike) or Reject (count it; may auto-block the account).
// ─────────────────────────────────────────────────────────────────────────

const REVIEW_STATUS_LABEL = {
  pending_review: { it: "In attesa", es: "Pendiente", color: "#F59E0B" },
  approved:       { it: "Approvato", es: "Aprobado",  color: "#22C55E" },
  rejected:       { it: "Rifiutato", es: "Rechazado", color: "#E11D48" },
};

const EVENT_TYPE_IT = {
  right_click: "Click destro",
  save_shortcut: "Scorciatoia Ctrl/Cmd+S",
  view_source: "Visualizza sorgente (Ctrl+U)",
  devtools_shortcut: "Scorciatoia DevTools (F12)",
  devtools_detected: "DevTools rilevati",
  context_menu: "Menu contestuale",
  drag_attempt: "Tentativo di trascinamento",
  print_attempt: "Tentativo di stampa (Ctrl+P)",
  screenshot_attempt: "Tentativo screenshot",
  window_blur_during_video: "Cambio scheda durante video",
  copy_attempt: "Tentativo di copia",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return String(iso).slice(0, 16); }
}

export function VIPAdminSecurityReviews() {
  const [statusFilter, setStatusFilter] = useState("pending_review");
  const [rows, setRows] = useState(null);
  const [count, setCount] = useState(0);
  const [busyId, setBusyId] = useState(null);

  const fetchRows = async (filter = statusFilter) => {
    setRows(null);
    try {
      const res = await apiGet(`/admin/security/reviews?status=${encodeURIComponent(filter)}&limit=200`);
      setRows(res.reviews || []);
      setCount(res.count || 0);
    } catch (e) {
      setRows([]);
      toast.error("Errore caricando i reclami");
    }
  };

  useEffect(() => { fetchRows(statusFilter); /* eslint-disable-next-line */ }, [statusFilter]);

  const decide = async (eventId, decision) => {
    setBusyId(eventId);
    try {
      const notes = window.prompt(
        decision === "approve"
          ? "Note (opzionale) per l'approvazione:"
          : "Note (opzionale) per il rifiuto:",
        ""
      );
      // null = user cancelled; "" = submitted empty → both fine
      if (notes === null) { setBusyId(null); return; }
      const path = `/admin/security/reviews/${encodeURIComponent(eventId)}/${decision}`;
      const res = await apiPost(path, { admin_notes: notes || null });
      toast.success(
        decision === "approve"
          ? `Reclamo approvato. Strike utente: ${res.strike_count}/${res.threshold}.`
          : `Reclamo rifiutato. Strike utente: ${res.strike_count}/${res.threshold}${res.is_blocked ? " · ACCOUNT BLOCCATO" : ""}.`
      );
      await fetchRows();
    } catch (e) {
      toast.error("Errore: " + (e?.response?.data?.detail || e.message));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="vip-admin-security-reviews">
      <div className="mb-6">
        <p className="text-[11px] uppercase tracking-[0.22em] mb-2"
           style={{ color: ADMIN.primary }}>
          Sicurezza · Reclami utenti
        </p>
        <h1 className="font-serif text-3xl mb-1" style={{ color: ADMIN.text }}>
          Reclami sicurezza
        </h1>
        <p className="text-sm" style={{ color: ADMIN.muted }}>
          Bandiera di reclami inviati dagli utenti dopo un avviso anti-pirateria.
          Approva per annullare lo strike o Rifiuta per contarlo (può bloccare l'account al 3° strike).
        </p>
      </div>

      <div className="flex items-center gap-2 mb-5 flex-wrap"
           data-testid="security-review-filters">
        {[
          { v: "pending_review", label: "In attesa" },
          { v: "approved",       label: "Approvati" },
          { v: "rejected",       label: "Rifiutati" },
          { v: "all",            label: "Tutti" },
        ].map((opt) => {
          const active = statusFilter === opt.v;
          return (
            <button
              key={opt.v}
              onClick={() => setStatusFilter(opt.v)}
              data-testid={`security-review-filter-${opt.v}`}
              className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all"
              style={{
                background: active ? ADMIN.primary : "white",
                color: active ? "white" : ADMIN.text,
                border: `1px solid ${active ? ADMIN.primary : ADMIN.border}`,
              }}
            >
              {opt.label}
            </button>
          );
        })}
        <span className="text-xs ml-auto" style={{ color: ADMIN.muted }}>
          {rows === null ? "Caricamento…" : `${count} reclami`}
        </span>
      </div>

      {rows === null ? (
        <div className="grid place-items-center py-20" data-testid="security-review-loading">
          <Loader2 className="h-6 w-6 animate-spin" style={{ color: ADMIN.primary }} />
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl p-8 text-center"
             style={{ background: ADMIN.card, border: `1px solid ${ADMIN.border}` }}
             data-testid="security-review-empty">
          <ShieldAlert className="h-9 w-9 mx-auto mb-3"
                       style={{ color: ADMIN.muted }} />
          <p className="text-sm" style={{ color: ADMIN.muted }}>
            Nessun reclamo in questa categoria.
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="security-review-list">
          {rows.map((r) => {
            const meta = REVIEW_STATUS_LABEL[r.status] || REVIEW_STATUS_LABEL.pending_review;
            const isPending = r.status === "pending_review";
            return (
              <div key={r.event_id}
                   data-testid={`security-review-row-${r.event_id}`}
                   className="rounded-2xl p-5 shadow-sm"
                   style={{ background: ADMIN.card,
                            border: `1px solid ${ADMIN.border}` }}>
                <div className="flex flex-wrap items-start gap-4 justify-between mb-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 rounded-full font-semibold"
                            style={{ background: `${meta.color}1A`, color: meta.color }}
                            data-testid={`security-review-status-${r.event_id}`}>
                        {meta.it}
                      </span>
                      <span className="text-[11px]" style={{ color: ADMIN.muted }}>
                        {fmtDate(r.appeal_submitted_at)}
                      </span>
                    </div>
                    <p className="font-medium text-base mb-0.5"
                       style={{ color: ADMIN.text }}>
                      {r.email || "—"}
                    </p>
                    <p className="text-[11px] font-mono break-all"
                       style={{ color: ADMIN.muted }}>
                      {r.user_id}
                    </p>
                  </div>
                  <div className="text-right text-xs space-y-1"
                       style={{ color: ADMIN.muted }}>
                    <div>
                      <strong style={{ color: ADMIN.text }}>Evento:</strong>{" "}
                      {EVENT_TYPE_IT[r.event_type] || r.event_type}
                    </div>
                    {r.lesson_id ? (
                      <div>
                        <strong style={{ color: ADMIN.text }}>Lezione:</strong>{" "}
                        <span className="font-mono">{r.lesson_id}</span>
                      </div>
                    ) : null}
                    <div>
                      <strong style={{ color: ADMIN.text }}>Strike precedenti:</strong>{" "}
                      <span className="font-mono">{r.prior_strike_count ?? 0}</span> / 3
                    </div>
                  </div>
                </div>

                <div className="rounded-xl p-3 mb-3"
                     style={{ background: ADMIN.primarySoft,
                              borderLeft: `3px solid ${ADMIN.primary}` }}>
                  <p className="text-[10px] uppercase tracking-[0.18em] mb-1"
                     style={{ color: ADMIN.primary }}>
                    Spiegazione utente
                  </p>
                  <p className="text-sm leading-relaxed whitespace-pre-wrap"
                     style={{ color: ADMIN.text }}
                     data-testid={`security-review-explanation-${r.event_id}`}>
                    {r.appeal_explanation || "—"}
                  </p>
                </div>

                {r.admin_notes ? (
                  <div className="rounded-xl p-2.5 mb-3 text-xs"
                       style={{ background: "#FFF7E0", border: "1px solid #F0DC9A",
                                color: "#6B4F00" }}>
                    <strong>Note admin:</strong> {r.admin_notes}
                  </div>
                ) : null}

                {isPending ? (
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => decide(r.event_id, "approve")}
                      disabled={busyId === r.event_id}
                      data-testid={`security-review-approve-${r.event_id}`}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium"
                      style={{
                        background: "#22C55E", color: "white",
                        opacity: busyId === r.event_id ? 0.7 : 1,
                      }}>
                      <CheckCircle2 className="h-4 w-4" /> Approva reclamo
                    </button>
                    <button
                      onClick={() => decide(r.event_id, "reject")}
                      disabled={busyId === r.event_id}
                      data-testid={`security-review-reject-${r.event_id}`}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium"
                      style={{
                        background: "#E11D48", color: "white",
                        opacity: busyId === r.event_id ? 0.7 : 1,
                      }}>
                      <AlertTriangle className="h-4 w-4" /> Rifiuta reclamo e conta strike
                    </button>
                  </div>
                ) : (
                  <p className="text-[11px]" style={{ color: ADMIN.muted }}>
                    Deciso il {fmtDate(r.reviewed_at)} da{" "}
                    <span className="font-mono">{r.reviewed_by_email || r.reviewed_by || "—"}</span>
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ============== Subscribers / Email Marketing ==============
const TIER_LABELS = {
  all: "Tutti",
  free: "Free",
  premium: "Premium",
  premium_plus: "Premium+",
  academy_active: "Academy attivi",
  academy_lifetime: "Academy a vita",
  academy_expired: "Academy scaduti",
};

const TIER_KINDS = {
  free: "muted",
  premium: "default",
  premium_plus: "warn",
  academy_active: "success",
  academy_lifetime: "success",
  academy_expired: "danger",
};

function fmtDateShort(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return "—"; }
}

export function VIPAdminSubscribers() {
  const [tier, setTier] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("promo_discount");
  const [previewLang, setPreviewLang] = useState("it");
  const [customSubject, setCustomSubject] = useState("");
  const [preview, setPreview] = useState(null);
  const [showCampaignPanel, setShowCampaignPanel] = useState(false);
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [recentCampaigns, setRecentCampaigns] = useState([]);

  const buildQuery = () => {
    const params = new URLSearchParams();
    if (tier && tier !== "all") params.append("tier", tier);
    if (dateFrom) params.append("date_from", dateFrom);
    if (dateTo) params.append("date_to", dateTo);
    if (q.trim()) params.append("q", q.trim());
    params.append("limit", "1000");
    return params.toString();
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const d = await apiGet(`/admin/vip/subscribers?${buildQuery()}`);
      setData(d);
      setSelectedIds(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento iscritti");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    apiGet("/admin/vip/marketing/templates").then((d) => setTemplates(d.templates || []));
    apiGet("/admin/vip/marketing/campaigns?limit=8").then((d) => setRecentCampaigns(d.campaigns || []));
  }, []);

  const exportCSV = () => {
    const url = `${API}/admin/vip/subscribers/export.csv?${buildQuery()}`;
    window.open(url, "_blank");
  };

  const loadPreview = async (key, lang) => {
    try {
      const url = `/admin/vip/marketing/preview?template_key=${key}&lang=${lang}${
        customSubject ? `&subject=${encodeURIComponent(customSubject)}` : ""
      }`;
      const d = await apiGet(url);
      setPreview(d);
    } catch {
      toast.error("Anteprima non disponibile");
    }
  };

  useEffect(() => {
    if (selectedTemplate && showCampaignPanel) {
      loadPreview(selectedTemplate, previewLang);
    }
    // eslint-disable-next-line
  }, [selectedTemplate, previewLang, customSubject, showCampaignPanel]);

  const toggleRow = (uid) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };

  const toggleAll = () => {
    if (!data?.users?.length) return;
    if (selectedIds.size === data.users.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(data.users.map((u) => u.user_id)));
    }
  };

  const sendCampaign = async (dryRun) => {
    if (!selectedTemplate) { toast.error("Seleziona un template"); return; }
    const useExplicit = selectedIds.size > 0;
    const payload = {
      template_key: selectedTemplate,
      dry_run: dryRun,
      ...(useExplicit
        ? { user_ids: Array.from(selectedIds) }
        : { filters: { tier, date_from: dateFrom || undefined,
                       date_to: dateTo || undefined,
                       search: q.trim() || undefined, limit: 5000 } }),
      ...(customSubject ? { subject: customSubject } : {}),
    };
    setSending(true); setLastResult(null);
    try {
      const result = await apiPost("/admin/vip/marketing/send-campaign", payload);
      setLastResult(result);
      if (dryRun) {
        toast.success(`Anteprima: ${result.sent_count} destinatari (${result.skipped_count} esclusi)`);
      } else {
        toast.success(`Inviate ${result.sent_count} email · ${result.error_count} errori`);
        apiGet("/admin/vip/marketing/campaigns?limit=8").then((d) => setRecentCampaigns(d.campaigns || []));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invio fallito");
    } finally { setSending(false); }
  };

  const rows = data?.users || [];
  const byTier = data?.by_tier || {};
  const total = data?.count || 0;
  const explicitMode = selectedIds.size > 0;

  return (
    <div className="space-y-6" data-testid="admin-subscribers-page">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Totale visibile" value={total} sub="dopo filtri" color={ADMIN.primary} />
        <Stat label="Free" value={byTier.free || 0} color={ADMIN.muted} />
        <Stat label="Premium" value={(byTier.premium || 0) + (byTier.premium_plus || 0)} color={ADMIN.warn} />
        <Stat label="Academy" value={(byTier.academy_active || 0) + (byTier.academy_lifetime || 0)} color={ADMIN.success} />
        <Stat label="Scaduti" value={byTier.academy_expired || 0} color={ADMIN.danger} />
      </div>

      <Section title="Filtri iscritti" action={
        <div className="flex gap-2">
          <Btn variant="ghost" icon={RefreshCw} onClick={fetchData} testId="subs-refresh" size="sm">Aggiorna</Btn>
          <Btn variant="soft" icon={FileDown} onClick={exportCSV} testId="subs-export-csv" size="sm">CSV</Btn>
        </div>
      }>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] block mb-1" style={{ color: ADMIN.muted }}>
              Stato abbonamento
            </label>
            <select value={tier} onChange={(e) => setTier(e.target.value)}
                    data-testid="subs-filter-tier"
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    style={{ borderColor: ADMIN.border, background: "white", color: ADMIN.text }}>
              {Object.entries(TIER_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] block mb-1" style={{ color: ADMIN.muted }}>
              Registrato dal
            </label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                   data-testid="subs-filter-from"
                   className="w-full border rounded-lg px-3 py-2 text-sm"
                   style={{ borderColor: ADMIN.border, color: ADMIN.text }} />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] block mb-1" style={{ color: ADMIN.muted }}>
              al
            </label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                   data-testid="subs-filter-to"
                   className="w-full border rounded-lg px-3 py-2 text-sm"
                   style={{ borderColor: ADMIN.border, color: ADMIN.text }} />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] block mb-1" style={{ color: ADMIN.muted }}>
              Cerca email / nome
            </label>
            <div className="flex gap-2">
              <input value={q} onChange={(e) => setQ(e.target.value)}
                     onKeyDown={(e) => e.key === "Enter" && fetchData()}
                     placeholder="es. mario@…"
                     data-testid="subs-filter-search"
                     className="w-full border rounded-lg px-3 py-2 text-sm"
                     style={{ borderColor: ADMIN.border, color: ADMIN.text }} />
              <Btn variant="primary" icon={Filter} onClick={fetchData} testId="subs-apply" size="sm">Filtra</Btn>
            </div>
          </div>
        </div>
      </Section>

      <div className="flex flex-wrap items-center gap-3 px-1">
        <p className="text-sm" style={{ color: ADMIN.muted }}>
          {explicitMode
            ? `${selectedIds.size} selezionati manualmente`
            : `Verrà usato il filtro corrente (${total} destinatari)`}
        </p>
        <div className="flex-1" />
        <Btn variant={showCampaignPanel ? "ghost" : "primary"} icon={Send}
             onClick={() => setShowCampaignPanel((v) => !v)}
             testId="subs-toggle-campaign">
          {showCampaignPanel ? "Chiudi panel" : "Invia campagna email"}
        </Btn>
      </div>

      {showCampaignPanel && (
        <Section title="Campagna email · template predefiniti" action={
          <Badge kind="warn">{previewLang.toUpperCase()}</Badge>
        }>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            <div className="md:col-span-4 space-y-3">
              <label className="text-[11px] uppercase tracking-[0.18em] block" style={{ color: ADMIN.muted }}>
                Template
              </label>
              <div className="space-y-2" data-testid="campaign-templates">
                {templates.map((t) => (
                  <button key={t.key} onClick={() => setSelectedTemplate(t.key)}
                          data-testid={`campaign-template-${t.key}`}
                          className="w-full text-left p-3 rounded-lg border transition-all"
                          style={{
                            borderColor: selectedTemplate === t.key ? ADMIN.primary : ADMIN.border,
                            background: selectedTemplate === t.key ? ADMIN.primarySoft : "white",
                          }}>
                    <p className="text-sm font-medium" style={{ color: ADMIN.text }}>
                      {t[previewLang]?.label || t.it.label}
                    </p>
                    <p className="text-[11px] mt-1" style={{ color: ADMIN.muted }}>
                      {t[previewLang]?.subject || t.it.subject}
                    </p>
                  </button>
                ))}
              </div>

              <div className="flex gap-2 mt-3">
                <button onClick={() => setPreviewLang("it")}
                        className="flex-1 px-3 py-1.5 rounded-lg text-xs font-medium border"
                        style={{
                          background: previewLang === "it" ? ADMIN.primary : "white",
                          color: previewLang === "it" ? "white" : ADMIN.muted,
                          borderColor: ADMIN.border,
                        }}>Italiano</button>
                <button onClick={() => setPreviewLang("es")}
                        className="flex-1 px-3 py-1.5 rounded-lg text-xs font-medium border"
                        style={{
                          background: previewLang === "es" ? ADMIN.primary : "white",
                          color: previewLang === "es" ? "white" : ADMIN.muted,
                          borderColor: ADMIN.border,
                        }}>Español</button>
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-[0.18em] block mb-1" style={{ color: ADMIN.muted }}>
                  Override subject (opzionale)
                </label>
                <input value={customSubject} onChange={(e) => setCustomSubject(e.target.value)}
                       placeholder={preview?.subject || "Usa il subject del template"}
                       data-testid="campaign-subject"
                       className="w-full border rounded-lg px-3 py-2 text-sm"
                       style={{ borderColor: ADMIN.border, color: ADMIN.text }} />
              </div>

              <div className="pt-3 border-t space-y-2" style={{ borderColor: ADMIN.border }}>
                <Btn variant="ghost" icon={Sparkles} onClick={() => sendCampaign(true)}
                     disabled={sending} testId="campaign-dryrun">
                  Calcola destinatari (dry run)
                </Btn>
                <Btn variant="success" icon={Send} onClick={() => {
                  if (!window.confirm(`Confermi invio della campagna a ${
                    explicitMode ? selectedIds.size : total
                  } destinatari?`)) return;
                  sendCampaign(false);
                }} disabled={sending} testId="campaign-send">
                  {sending ? "Invio in corso…" : "Invia ora"}
                </Btn>
                <p className="text-[10px] leading-relaxed" style={{ color: ADMIN.muted }}>
                  Gli utenti disiscritti vengono automaticamente esclusi.
                  L'invio rispetta il limite di Resend (~1 email/sec).
                </p>
              </div>
            </div>

            <div className="md:col-span-8">
              <label className="text-[11px] uppercase tracking-[0.18em] block mb-2" style={{ color: ADMIN.muted }}>
                Anteprima ({preview?.subject || "…"})
              </label>
              <div className="rounded-lg border overflow-hidden bg-white" style={{ borderColor: ADMIN.border }}>
                {preview?.html ? (
                  <iframe srcDoc={preview.html} title="Email preview"
                          data-testid="campaign-preview-iframe"
                          className="w-full" style={{ height: 560, border: 0 }} />
                ) : (
                  <div className="p-8 text-center text-sm" style={{ color: ADMIN.muted }}>
                    Seleziona un template per vedere l'anteprima.
                  </div>
                )}
              </div>
            </div>
          </div>

          {lastResult && (
            <div className="mt-4 p-3 rounded-lg border" style={{
              borderColor: ADMIN.border,
              background: lastResult.dry_run ? ADMIN.primarySoft : "#E5F6EE",
            }}>
              <p className="text-sm font-medium" style={{ color: ADMIN.text }}>
                {lastResult.dry_run ? "Dry-run" : "Campagna inviata"} ·
                <span className="font-mono ml-2 text-xs">{lastResult.campaign_id}</span>
              </p>
              <div className="flex gap-4 mt-1 text-xs" style={{ color: ADMIN.muted }}>
                <span>Inviate: <strong style={{ color: ADMIN.success }}>{lastResult.sent_count}</strong></span>
                <span>Escluse: <strong>{lastResult.skipped_count}</strong></span>
                <span>Errori: <strong style={{ color: ADMIN.danger }}>{lastResult.error_count}</strong></span>
              </div>
            </div>
          )}
        </Section>
      )}

      {recentCampaigns.length > 0 && (
        <Section title="Ultime campagne inviate">
          <div className="space-y-1">
            {recentCampaigns.map((c) => (
              <div key={c.campaign_id} className="flex items-center justify-between p-2 rounded-lg hover:bg-purple-50">
                <div className="flex-1">
                  <p className="text-sm font-medium" style={{ color: ADMIN.text }}>
                    {c.template_key} · <span className="font-mono text-xs">{c.campaign_id}</span>
                  </p>
                  <p className="text-[11px]" style={{ color: ADMIN.muted }}>
                    {fmtDateShort(c.started_at)} · da {c.admin_email}
                  </p>
                </div>
                <div className="flex gap-2 text-xs">
                  <Badge kind="success">{c.sent_count} inviate</Badge>
                  {c.error_count > 0 ? <Badge kind="danger">{c.error_count} errori</Badge> : null}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section title={`Iscritti (${total})`}>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin" style={{ color: ADMIN.primary }} />
          </div>
        ) : rows.length === 0 ? (
          <p className="text-center py-12 text-sm" style={{ color: ADMIN.muted }}>
            Nessun iscritto corrisponde ai filtri.
          </p>
        ) : (
          <div className="overflow-x-auto" data-testid="subs-table">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: ADMIN.primarySoft, color: ADMIN.muted }}>
                  <th className="text-left px-3 py-2 w-10">
                    <input type="checkbox"
                           checked={selectedIds.size > 0 && selectedIds.size === rows.length}
                           onChange={toggleAll}
                           data-testid="subs-select-all" />
                  </th>
                  <th className="text-left px-3 py-2">Email</th>
                  <th className="text-left px-3 py-2">Nome</th>
                  <th className="text-left px-3 py-2">Tier</th>
                  <th className="text-left px-3 py-2">Registrato</th>
                  <th className="text-left px-3 py-2">Ultimo accesso</th>
                  <th className="text-left px-3 py-2">Lingua</th>
                  <th className="text-left px-3 py-2">Stato</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => (
                  <tr key={u.user_id}
                      className="border-t hover:bg-purple-50"
                      style={{ borderColor: ADMIN.border }}>
                    <td className="px-3 py-2">
                      <input type="checkbox" checked={selectedIds.has(u.user_id)}
                             onChange={() => toggleRow(u.user_id)}
                             data-testid={`subs-row-check-${u.user_id}`} />
                    </td>
                    <td className="px-3 py-2 font-mono text-xs" style={{ color: ADMIN.text }}>
                      <Link to={`/admin/vip/users/${u.user_id}`}
                            className="hover:underline"
                            style={{ color: ADMIN.primary }}
                            data-testid={`subs-row-link-${u.user_id}`}>
                        {u.email}
                      </Link>
                      {u.is_admin ? <span className="ml-2"><Badge kind="warn">admin</Badge></span> : null}
                    </td>
                    <td className="px-3 py-2" style={{ color: ADMIN.text }}>{u.name || "—"}</td>
                    <td className="px-3 py-2">
                      <Badge kind={TIER_KINDS[u.tier] || "muted"}>
                        {TIER_LABELS[u.tier] || u.tier}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-xs" style={{ color: ADMIN.muted }}>
                      {fmtDateShort(u.created_at)}
                    </td>
                    <td className="px-3 py-2 text-xs" style={{ color: ADMIN.muted }}>
                      {fmtDateShort(u.last_seen_at)}
                    </td>
                    <td className="px-3 py-2 text-xs uppercase" style={{ color: ADMIN.muted }}>
                      {u.preferred_language}
                    </td>
                    <td className="px-3 py-2">
                      {u.unsubscribed ? <Badge kind="danger">disisc.</Badge> : <Badge kind="success">attivo</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}

// ============== Device Fingerprints / Anti-abuse ==============
export function VIPAdminDevices() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all"); // all | suspicious | consumed
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (filter === "suspicious") params.append("suspicious_only", "true");
      if (filter === "consumed") params.append("consumed_only", "true");
      const d = await apiGet(`/admin/vip/devices?${params.toString()}`);
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento dispositivi");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [filter]);

  const unlock = async (fp) => {
    if (!window.confirm(
      `Confermi lo sblocco del dispositivo ${fp.fingerprint_id}?\n` +
      `Permetterà a un nuovo utente di fare la prova introduttiva da questo dispositivo.`
    )) return;
    setBusyId(fp.fingerprint_id);
    try {
      await apiPost(`/admin/vip/devices/${fp.fingerprint_id}/unlock`);
      toast.success("Dispositivo sbloccato");
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sblocco fallito");
    } finally { setBusyId(null); }
  };

  const rows = data?.devices || [];
  const total = data?.count || 0;
  const suspiciousCount = rows.filter((r) => r.suspicious_flag).length;
  const consumedCount = rows.filter((r) => r.free_exam_consumed).length;
  const unlockedCount = rows.filter((r) => r.unlocked_by_admin).length;

  return (
    <div className="space-y-6" data-testid="admin-devices-page">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Totale dispositivi" value={total} sub={filter !== "all" ? "filtrati" : ""}
              color={ADMIN.primary} />
        <Stat label="Hanno usato free" value={consumedCount} color={ADMIN.warn} />
        <Stat label="Flag sospetti (≥3)" value={suspiciousCount} color={ADMIN.danger} />
        <Stat label="Sbloccati da admin" value={unlockedCount} color={ADMIN.success} />
      </div>

      <Section title="Filtri" action={
        <Btn variant="ghost" icon={RefreshCw} onClick={fetchData} size="sm">Aggiorna</Btn>
      }>
        <div className="flex flex-wrap gap-2">
          {[
            { k: "all", label: "Tutti" },
            { k: "consumed", label: "Free intro consumato" },
            { k: "suspicious", label: "Solo sospetti (≥3 account)" },
          ].map(({ k, label }) => (
            <button key={k}
                    onClick={() => setFilter(k)}
                    data-testid={`devices-filter-${k}`}
                    className="px-3 py-1.5 rounded-full text-sm font-medium border"
                    style={{
                      background: filter === k ? ADMIN.primary : "white",
                      color: filter === k ? "white" : ADMIN.muted,
                      borderColor: ADMIN.border,
                    }}>
              {label}
            </button>
          ))}
        </div>
      </Section>

      <Section title={`Dispositivi (${total})`}>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin" style={{ color: ADMIN.primary }} />
          </div>
        ) : rows.length === 0 ? (
          <p className="text-center py-12 text-sm" style={{ color: ADMIN.muted }}>
            Nessun dispositivo corrisponde al filtro.
          </p>
        ) : (
          <div className="space-y-3" data-testid="devices-list">
            {rows.map((r) => (
              <div key={r.fingerprint_id}
                   data-testid={`device-row-${r.fingerprint_id}`}
                   className="rounded-xl border p-4"
                   style={{
                     borderColor: r.suspicious_flag ? "#FCA5A5" : ADMIN.border,
                     background: r.suspicious_flag ? "#FEF2F2" : "white",
                   }}>
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-lg grid place-items-center shrink-0"
                       style={{ background: ADMIN.primarySoft, color: ADMIN.primary }}>
                    <Smartphone className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="font-mono text-xs" style={{ color: ADMIN.text }}>
                        {r.fingerprint_id}
                      </span>
                      {r.suspicious_flag ? <Badge kind="danger">Sospetto</Badge> : null}
                      {r.free_exam_consumed ? <Badge kind="warn">Free consumato</Badge> : <Badge kind="success">Disponibile</Badge>}
                      {r.unlocked_by_admin ? <Badge kind="success">Sbloccato</Badge> : null}
                      <Badge kind="muted">{r.user_count} account</Badge>
                    </div>
                    <div className="text-[11px] mt-1" style={{ color: ADMIN.muted }}>
                      UA: <span className="font-mono">{(r.components_sample?.user_agent || "").slice(0, 80)}</span>
                    </div>
                    <div className="text-[11px] mt-0.5" style={{ color: ADMIN.muted }}>
                      {r.components_sample?.timezone || "—"} · {r.components_sample?.screen || "—"} · {r.components_sample?.language || "—"} · {r.last_ip_prefix || "ip ?"}
                    </div>
                    <div className="text-[11px] mt-0.5" style={{ color: ADMIN.muted }}>
                      Ultimo accesso: {r.last_seen_at ? new Date(r.last_seen_at).toLocaleString("it-IT") : "—"}
                    </div>

                    {/* Linked users */}
                    {(r.users || []).length > 0 && (
                      <div className="mt-2 space-y-1 border-t pt-2"
                           style={{ borderColor: ADMIN.border }}>
                        {r.users.map((u) => (
                          <div key={u.user_id} className="flex items-center gap-2 text-xs"
                               style={{ color: ADMIN.text }}>
                            <span className="font-mono">{u.email || u.user_id}</span>
                            {u.is_admin ? <Badge kind="warn">admin</Badge> : null}
                            {r.free_exam_consumed_by === u.user_id ? <Badge kind="muted">free</Badge> : null}
                          </div>
                        ))}
                      </div>
                    )}

                    {r.free_exam_consumed && (
                      <div className="mt-3">
                        <Btn variant="success" icon={Unlock}
                             onClick={() => unlock(r)}
                             disabled={busyId === r.fingerprint_id}
                             testId={`device-unlock-${r.fingerprint_id}`}
                             size="sm">
                          {busyId === r.fingerprint_id ? "Sblocco…" : "Sblocca free intro"}
                        </Btn>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

